import * as React from 'react';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  CustomMarker,
  CustomTextMarker,
  DIGESTION_FIELD_IDS,
  matchesExpectedText,
  FurtherConsiderations,
  MenstrualPhase,
  PanelField,
  PanelMarker,
  practitionerSectionFromOptionValue,
  practitionerSectionKey,
  practitionerSectionOptionValue,
  practitionerSectionTitle,
  SECTION_PANEL_MARKER_GETTERS,
} from '../../models';

import { FurtherConsiderationsEntryStore } from '../../stores/further-considerations-entry-store';
import { DisclaimerSection } from '../bloodworkz/disclaimer-section';
import { Card, Table, OverlayTrigger, Tooltip, Popover, Button, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload, faFileCode } from '@fortawesome/pro-regular-svg-icons';
import { MathUtils } from '../../utils/MathUtils';
import { visibleExceptProd } from '../../utils/featureVisibility';
import { digestionVisible } from '../../utils/digestionVisibility';
import Moment from 'react-moment';
import moment from 'moment';
import { UserProfile } from '../../models/UserProfile';
import { resolveComparisonReportTitle } from '../../models/reportTitles';
import { UserProfileUIStore } from '../../stores/user-profile-ui-store';
import Storage from '@aws-amplify/storage';
import { PDFModal } from '../common/pdf-modal';
import { GenericModal } from '../common/generic-modal';
import { buildAIExportData } from '../../helpers/AIExportBuilder';
import { AI_PROGRESS_SUMMARY_PROMPT } from '../../helpers/aiReviewPrompt';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { ClientEntryStore } from '../../stores/client-entry-store';
import { ClientNotes } from './client-notes';
import {
  renderNoteLine,
  MarkdownToolbar,
  NOTE_TEXT_COLOR,
  wrapSelection,
  formatMarkerForKeydown,
} from './note-markdown';
import { SectionNotesSummaryPanel } from './section-notes-summary-panel';
import { scrollPanelIntoView } from '../../utils/scrollPanelIntoView';
import { isSameBloodwork } from '../../utils/sameBloodwork';
import { aiProgressSummaryVisible } from '../../utils/aiSummaryVisibility';
import { Section } from '../../models';
import { faFileDownload as faFileDownloadSolid, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

/**
 * The report section behind each comparison-section key, so a custom marker
 * filed under a section on the analysis page lands in the same section's
 * comparison table. Keys are the ones in comparisonSectionOrder().
 *
 * 'customMarkers' is absent on purpose — it is the appendix that holds whatever
 * was NOT placed, so it can never be a placement target.
 */
const COMPARISON_SECTION_BY_KEY: { [key: string]: Section } = {
  rbc: Section.RBC,
  iron: Section.IRON,
  immune: Section.WBC,
  platelets: Section.PLATELETS,
  glucose: Section.GLUCOSE,
  kidney: Section.KIDNEY,
  proteins: Section.PROTEINS,
  digestion: Section.DIGESTION,
  nutrients: Section.NUTRIENTS,
  electrolytes: Section.ELECTROLYTES,
  lipids: Section.LIPIDS,
  inflammation: Section.INFLAMMATION_OXIDATIVE_STRESS,
  liver: Section.LIVER_BILIARY,
  thyroid: Section.THYROID,
  stressAdrenal: Section.STRESS_ADRENAL,
  hormones: Section.SEX_HORMONE,
  prostate: Section.PROSTATE,
};

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');
const thumbs_up = require('../../assets/imgs/thumbs-up-15x15.png');
const thumbs_down = require('../../assets/imgs/thumbs-down-15x15.png');
const logo = require('../../assets/imgs/logo.png');

const NUM_RESULTS_TO_COMPARE: number = 10;
const NUM_RESULTS_TO_COMPARE_FOR_PDF: number = 5;

// Breathing room above a panel that starts a PDF page (the Insights table and
// Overall Progress & Trends chunks each open with a forced page break, so this
// sits at the very top of the page). Matches the Disclaimer wrapper's
// padding-top so all three panels clear the page edge by the same amount.
// Counted in the chunkers' HEADER_HEIGHT — raising it here without raising
// that would let a chunk run past the bottom of the page.
const TOP_OF_PAGE_SPACER = '24px';

// Short prompt shown in the "Export Raw Data" info popover for practitioners to
// copy and use with the exported file in an AI assistant.
const AI_FILE_PROMPT =
  'You are a functional-medicine analyst. Using ONLY the attached LabSmarts blood ' +
  'work export, write a BRIEF practitioner summary — synthesize what matters and ' +
  'what changed; do NOT re-list values (the practitioner already has them in the ' +
  'report and the Markers-Outside-Optimal summary). Cover: a one-line snapshot; ' +
  '3–5 top findings that group related markers into one insight (drawn from the ' +
  'insight panels, the 5-system Results Overview scores/colors, and section notes — ' +
  'cover every flagged body system, including ones without detailed patterns); ' +
  'notable changes across tests; and any brief follow-ups. Decision-support, not a diagnosis.';

/** Button that copies the given text to the clipboard with brief "Copied!" feedback. */
const CopyPromptButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  const onCopy = () => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => undefined);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline-secondary"
      onClick={onCopy}
      style={{ marginTop: '8px' }}
    >
      {copied ? 'Copied!' : 'Copy prompt'}
    </Button>
  );
};

interface Props {
  bloodworks: Bloodwork[];
  bloodworkRanges: BloodworkRange[];
  currentUserProfile: UserProfile;
  onExportToPdf: Function;
  onHidePdfModal: Function;
  bloodworkEntryStore: BloodworkEntryStore;
  clientEntryStore: ClientEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  userProfileUIStore?: UserProfileUIStore;
}

type State = {
  showPdfModal: boolean;
  showUpgradeModal: boolean;
  numResultsToCompare: number;
  headerLogoSrc?: string; // resolved preview URL for the modal's <Image id="logo">
  showHiddenRows: boolean; // toggle to show/hide rows with no value
  withNotes: boolean; // toggle to include practitioner notes in PDF (changed name to Insights)
  showSectionNotes: boolean; // toggle the Section Notes column in the notes table (screen + PDF)
  furtherConsiderations: FurtherConsiderations;
  generatingSummary: boolean; // AI "Overall Progress & Trends" request in flight
  aiSummaryDraft: string; // latest AI-generated draft to inject into the notes field
};

// What the comparison report shows for one collection date. The Insights note
// (Section.NOTES) is only half of it: a practitioner can type everything in the
// per-section Notes tabs and never touch Insights, and those notes have to come
// across too — otherwise that test looks like it has no notes at all.
/**
 * The history newest test first, on a COPY.
 *
 * Which end of the history a marker's position comes from is a decision, not a
 * detail. Custom markers are gathered across every test in the comparison, and
 * gathering them oldest-first pinned each one's row to the order it was first
 * typed in, years ago — later tests could correct its label but never move it.
 * That left the practitioner's own ordering (the drag handles in the entry
 * table, which set the order on the analysis screen and in the PDF) unable to
 * reach the comparison report at all.
 *
 * Newest first, and first-seen wins, so the newest test that carries a marker
 * decides where it sits. A marker dropped from recent tests keeps a place among
 * the ones it was last filed with, rather than being pushed to the end.
 *
 * A COPY because Array.reverse mutates, and this history is shared by every
 * section builder on the page.
 */
function newestFirst(history: BloodworkAnalysis[]): BloodworkAnalysis[] {
  return [...history].reverse();
}

function insightsTextFor(bloodwork: Bloodwork): string {
  return (bloodwork?.bloodworkNotes?.findNote(Section.NOTES)?.text || '').trim();
}

// That test's consolidated Section Notes — the practitioner's edited override
// when there is one, otherwise each section's own note under its "## Label".
function sectionNotesTextFor(bloodworkAnalysis: BloodworkAnalysis): string {
  return SectionNotesSummaryPanel.bodyText(bloodworkAnalysis).trim();
}

// Insights first, then the section notes beneath it. Either half on its own is
// enough to give the date a row.
export function comparisonNotesTextFor(
  bloodworkAnalysis: BloodworkAnalysis,
): string {
  return [
    insightsTextFor(bloodworkAnalysis?.bloodwork),
    sectionNotesTextFor(bloodworkAnalysis),
  ]
    .filter(Boolean)
    .join('\n\n');
}

// One collection date's notes as the PDF renders them: each column already
// sliced to what fits on this page. isCont marks the continuation halves.
export type NoteChunkRow = {
  collectionDate: string;
  insightsSplit: string;
  sectionSplit: string;
  isCont: boolean;
};

/**
 * Pre-paginate the notes table for the PDF: an array of page chunks, each an
 * array of rows. html2pdf can't split a row (and the date cell can't be
 * rowSpanned across a break), so both columns are sliced against the SAME page
 * budget — a row that spills carries both halves onto the next page in step,
 * otherwise one column would run ahead and pair the wrong text with the wrong
 * date. Row height is the taller column, not the sum of the two.
 */
export function paginateNoteRows(
  entries: {
    collectionDate: string;
    insights: string;
    sectionNotes: string;
  }[],
  opts: { showSectionNotes: boolean },
): NoteChunkRow[][] {
  // PAGE GEOMETRY, measured off a real export rather than guessed. html2pdf
  // renders into a container whose CSS width is pageSize.inner.width in mm
  // (worker.js:110) and its jsPDF format defaults to a4 — so landscape a4
  // (297x210mm) less the [3,5,10,5] margins set in PDFModal gives a 287x197mm
  // inner box: ~1085 CSS px wide, ~744 CSS px tall per page.
  // All the safety margin lives HERE, in the page budget — never in WRAP_CHARS
  // below. A too-small divisor is wrong once per line and compounds: at 74
  // instead of 82, a 163-character bullet scores 3 lines instead of 2, and 8
  // such bullets lose a third of a page. 44px of reserve absorbs a couple of
  // under-estimated lines instead.
  const MAX_PAGE_HEIGHT = 700;
  // Top spacer (24px, see TOP_OF_PAGE_SPACER) + card header + table header.
  const HEADER_HEIGHT = 76;
  const ROW_PADDING = 12; // Internal padding per row
  const LINE_HEIGHT = 18; // Height per line; measured ~17.7, so it leans safe
  // Characters on one rendered line, per column — the REAL figure, no cushion:
  // the column's share of the ~1083px table, less 24px of cell padding, over
  // the ~5.6px average character width. 45% -> 82, 40% -> 73, 80% -> 150.
  const INS_WRAP = opts.showSectionNotes ? 82 : 150;
  const SEC_WRAP = 73;

  // Take as many lines as fit in `budget` and hand back the rest for the next
  // page. forceOne stops a single over-tall line from stalling a fresh page.
  const takeLines = (
    lines: string[],
    budget: number,
    forceOne: boolean,
    wrapChars: number,
  ) => {
    const taken: string[] = [];
    let height = 0;
    let i = 0;
    while (i < lines.length) {
      const h =
        Math.max(1, Math.ceil(lines[i].length / wrapChars)) * LINE_HEIGHT;
      if (height + h > budget && (taken.length > 0 || !forceOne)) break;
      taken.push(lines[i]);
      height += h;
      i++;
    }
    return { taken, rest: lines.slice(i), height };
  };

  const noteChunks: NoteChunkRow[][] = [];
  let currentChunk: NoteChunkRow[] = [];
  let currentHeight = HEADER_HEIGHT;

  entries.forEach(entry => {
    let insLines = entry.insights ? entry.insights.split('\n') : [];
    let secLines =
      opts.showSectionNotes && entry.sectionNotes
        ? entry.sectionNotes.split('\n')
        : [];
    if (insLines.length === 0 && secLines.length === 0) return;

    // If the current chunk is too full to fit even two more lines, flush it
    // before starting a new note so the next collection date starts on a fresh page.
    if (
      currentChunk.length > 0 &&
      currentHeight + LINE_HEIGHT * 2 > MAX_PAGE_HEIGHT
    ) {
      noteChunks.push(currentChunk);
      currentChunk = [];
      currentHeight = HEADER_HEIGHT;
    }

    let partsAdded = 0;
    while (true) {
      const budget = MAX_PAGE_HEIGHT - currentHeight;
      const fresh = currentChunk.length === 0;
      const ins = takeLines(insLines, budget, fresh, INS_WRAP);
      const sec = takeLines(secLines, budget, fresh, SEC_WRAP);

      if (ins.taken.length === 0 && sec.taken.length === 0) {
        // Nothing fits in what's left of this page — start a fresh one. (Can't
        // loop forever: on a fresh chunk `forceOne` always takes a line.)
        noteChunks.push(currentChunk);
        currentChunk = [];
        currentHeight = HEADER_HEIGHT;
        continue;
      }

      currentChunk.push({
        collectionDate: entry.collectionDate,
        insightsSplit: ins.taken.join('\n'),
        sectionSplit: sec.taken.join('\n'),
        isCont: partsAdded > 0,
      });
      currentHeight += Math.max(ins.height, sec.height) + ROW_PADDING;
      insLines = ins.rest;
      secLines = sec.rest;
      if (insLines.length === 0 && secLines.length === 0) break;

      noteChunks.push(currentChunk);
      currentChunk = [];
      currentHeight = HEADER_HEIGHT;
      partsAdded++;
    }
  });

  if (currentChunk.length > 0) {
    noteChunks.push(currentChunk);
  }

  return noteChunks;
}

class PractitionerNoteRow extends React.Component<
  {
    bloodwork: Bloodwork;
    bloodworkEntryStore: BloodworkEntryStore;
    /**
     * The analysis this row's notes belong to. Needed to edit Section Notes:
     * that cell is not a stored field but the consolidated body, and saving it
     * writes the same Section.SECTION_NOTES override the panel on that test's
     * own report writes.
     */
    bloodworkAnalysis?: BloodworkAnalysis;
    sectionNotesText?: string;
    showSectionNotesColumn?: boolean;
  },
  { isEditing: boolean; text: string; sectionNotesDraft: string }
> {
  constructor(props: any) {
    super(props);
    const note = props.bloodwork.bloodworkNotes?.findNote(Section.NOTES);
    this.state = {
      isEditing: false,
      text: note ? note.text : '',
      sectionNotesDraft: props.sectionNotesText || '',
    };
  }

  textareaRef = React.createRef<HTMLTextAreaElement>();
  sectionNotesRef = React.createRef<HTMLTextAreaElement>();
  // This note's own row — where Save scrolls back to.
  rowRef = React.createRef<HTMLTableRowElement>();

  componentDidUpdate(prevProps: any, prevState: any) {
    if (this.state.isEditing && !prevState.isEditing) {
      [this.textareaRef.current, this.sectionNotesRef.current].forEach(el => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      });
    }
  }
  componentWillReceiveProps(nextProps: any) {
    // A row pointed at a DIFFERENT test reseeds even mid-edit: what was typed
    // belongs to the test that was there, and Save would write it over this
    // one's note. Rows are keyed by test id, so this is the safety net rather
    // than the normal path.
    const switchedTest = !isSameBloodwork(
      nextProps.bloodwork,
      this.props.bloodwork,
    );
    if (!this.state.isEditing || switchedTest) {
      const note = nextProps.bloodwork.bloodworkNotes?.findNote(Section.NOTES);
      this.setState({
        isEditing: switchedTest ? false : this.state.isEditing,
        text: note ? note.text : '',
        sectionNotesDraft: nextProps.sectionNotesText || '',
      });
    }
  }
  handleEdit = () =>
    this.setState({
      isEditing: true,
      sectionNotesDraft: this.props.sectionNotesText || '',
    });

  // Same Bold/Italic/Underline tools as the Insights panel this note is typed
  // in — it's the same note (Section.NOTES), just shown per collection date.
  applyFormat = (value: string, selStart: number, selEnd: number) => {
    this.setState({ text: value }, () => {
      const el = this.textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(selStart, selEnd);
      });
    });
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const marker = formatMarkerForKeydown(e);
    if (marker && this.textareaRef.current) {
      e.preventDefault();
      const { value, selStart, selEnd } = wrapSelection(
        this.textareaRef.current,
        marker,
      );
      this.applyFormat(value, selStart, selEnd);
    }
  };

  handleSave = (e: any) => {
    e.preventDefault();
    const { bloodwork, bloodworkEntryStore, bloodworkAnalysis } = this.props;
    let note = bloodwork.bloodworkNotes?.findNote(Section.NOTES);
    if (!note) {
      note = bloodwork.bloodworkNotes?.addNote(Section.NOTES, '');
    }
    if (note) note.text = this.state.text;
    // Section Notes go through the panel's own writer, so an edit made here
    // and one made on that test's report produce exactly the same record — one
    // Section.SECTION_NOTES override, and none at all when nothing changed.
    if (bloodworkAnalysis && this.canEditSectionNotes()) {
      SectionNotesSummaryPanel.writeOverrideOn(
        bloodworkAnalysis,
        this.state.sectionNotesDraft,
      );
    }
    // Scroll back to this row once the save resolves — the spinner modal locks
    // body scrolling while it's up, so without this the page lands at the top
    // of the report.
    const scrollBack = () => scrollPanelIntoView(this.rowRef.current);
    bloodworkEntryStore.saveBloodwork(bloodwork)
      .then(scrollBack)
      .catch((err) => {
        console.error('Failed to save bloodwork note', err);
        alert('Failed to save your note. Please try again.');
        scrollBack();
      });
    this.setState({ isEditing: false });
  };
  handleCancel = () => {
    const note = this.props.bloodwork.bloodworkNotes?.findNote(Section.NOTES);
    this.setState({
      isEditing: false,
      text: note ? note.text : '',
      sectionNotesDraft: this.props.sectionNotesText || '',
    });
  };

  /** Section Notes are editable only where the column is shown at all. */
  private canEditSectionNotes = (): boolean =>
    !!this.props.showSectionNotesColumn && !!this.props.bloodworkAnalysis;

  /**
   * Drop the edit back to each section's own notes, the same Reset the panel on
   * that test's report offers. Only meaningful once an override exists.
   */
  handleResetSectionNotes = () => {
    const { bloodworkAnalysis } = this.props;
    if (!bloodworkAnalysis) {
      return;
    }
    if (
      !window.confirm(
        'Clear your edits and rebuild Section Notes from each section’s notes?',
      )
    ) {
      return;
    }
    this.setState({
      sectionNotesDraft: SectionNotesSummaryPanel.autoText(bloodworkAnalysis),
    });
  };

  render() {
    const {
      bloodwork,
      bloodworkAnalysis,
      sectionNotesText,
      showSectionNotesColumn,
    } = this.props;
    const { isEditing, text, sectionNotesDraft } = this.state;
    const canEditSectionNotes = this.canEditSectionNotes();
    const hasSectionNotesOverride =
      !!bloodworkAnalysis &&
      SectionNotesSummaryPanel.hasOverride(bloodworkAnalysis);
    return (
      <tr ref={this.rowRef}>
        <td>
          <Moment format="MM/DD/YYYY">{bloodwork.collectionDate}</Moment>
        </td>
        <td>
          {isEditing ? (
            <>
              <MarkdownToolbar
                getTextarea={() => this.textareaRef.current}
                onApply={this.applyFormat}
              />
              <textarea
                ref={this.textareaRef}
                value={text}
                onChange={e => {
                  this.setState({ text: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={this.handleKeyDown}
                style={{
                  width: '100%',
                  minHeight: '60px',
                  overflow: 'hidden',
                  resize: 'none',
                  // A raw textarea keeps the browser's own font, so it read
                  // bigger (and in a different face) than the formatted note it
                  // turns into. Match the page.
                  fontFamily: 'inherit',
                  fontSize: '12px',
                }}
              />
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: 4 }}>
                Formatting:&nbsp;<code>## Section</code>&nbsp;for section
                headings,&nbsp;<code>**bold**</code>,&nbsp;<code>*italic*</code>
                ,&nbsp;<code>_underline_</code>. These render formatted when you
                Save.
              </div>
            </>
          ) : (
            // Formatted, not raw markup — a note typed with **bold** in the
            // Insights panel has to read the same here. Line by line, because
            // the PDF build of this table paginates it that way.
            <div
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                minHeight: '1.5em',
                color: NOTE_TEXT_COLOR,
              }}
            >
              {(text || '').trim() ? (
                (text || '').split('\n').map((line, lIdx) => (
                  <div key={lIdx}>{renderNoteLine(line)}</div>
                ))
              ) : (
                // A test can have section notes and no Insights note — say so
                // rather than leaving a cell that reads as broken.
                <span style={{ color: '#adb5bd' }}>&mdash;</span>
              )}
            </div>
          )}
        </td>
        {/* Section Notes ride in their own column so they never read as part of
            the Insights note, which summarises the whole test. Editing them here
            writes the same consolidated override the panel on that test's own
            report writes, so the two screens can never disagree about what the
            Section Notes say. */}
        {showSectionNotesColumn && (
          <td>
            {isEditing && canEditSectionNotes ? (
              <>
                <textarea
                  ref={this.sectionNotesRef}
                  value={sectionNotesDraft}
                  onChange={e => {
                    this.setState({ sectionNotesDraft: e.target.value });
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    overflow: 'hidden',
                    resize: 'none',
                    fontFamily: 'inherit',
                    fontSize: '12px',
                  }}
                />
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: 4 }}>
                  Built from each section&rsquo;s own notes. Editing it here
                  keeps what you type, on this test only, until you reset it.
                  {hasSectionNotesOverride || sectionNotesDraft ? (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 align-baseline"
                        style={{ fontSize: '12px' }}
                        onClick={this.handleResetSectionNotes}
                      >
                        Reset to the section notes
                      </button>
                    </>
                  ) : null}
                </div>
              </>
            ) : (sectionNotesText || '').trim() ? (
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: NOTE_TEXT_COLOR,
                }}
              >
                {sectionNotesText!.split('\n').map((line, lIdx) => (
                  <div key={`sn-${lIdx}`}>{renderNoteLine(line)}</div>
                ))}
              </div>
            ) : (
              <span style={{ color: '#adb5bd' }}>&mdash;</span>
            )}
          </td>
        )}
        <td style={{ textAlign: 'center' }}>
          {isEditing ? (
            <>
              <button
                className="btn btn-sm btn-primary"
                onClick={this.handleSave}
                style={{ marginRight: '4px' }}
              >
                Save
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={this.handleCancel}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              className="btn btn-sm btn-primary"
              onClick={this.handleEdit}
            >
              Edit
            </button>
          )}
        </td>
      </tr>
    );
  }
}

// Max compact Hormone Balance rows that fit beneath the section heading +
// column header on one landscape PDF page. The retired lab-report layout packed up to 19
// compact hormone rows *plus* the Prostate panel (3 rows) onto a single page,
// so ~22 compact hormone rows alone — with even less overhead (one title, one
// column header) — clear a page without being sliced mid-row by html2pdf.
// With Cortisol AM/PM, DHEA-S and Pregnenolone moved out to Stress & Adrenal,
// the full hormone set is at most 22 rows (menstrual phase + 15 hormones + the
// 2 BioT + 2 FreeT + 2 TotalT markers) when "Hide blank rows" is unchecked, so
// the threshold is raised to 24 to keep the whole table — including the two
// FreeT and two TotalT markers — on ONE page instead of spilling them onto a
// second chunk. Drives both the page-chunking of the table
// (renderHormoneSectionPdf) and the "pack Prostate on the same page" rule.
const HORMONE_ROWS_PER_PDF_PAGE = 24;

export class BloodworkComparisonAnalysis extends React.Component<Props, State> {
  // Anchors the "Test Results Comparison" card so we can restore scroll to its
  // top after a toggle re-orders/re-renders the sections (otherwise the page
  // jumps mid-table to a section like "Blood Oxygen & Red Blood Cell Function").
  private comparisonCardRef = React.createRef<HTMLDivElement>();

  /**
   * Scroll the comparison card title back to the top of the viewport, offset by
   * the sticky header so the title isn't hidden beneath it. Mirrors the
   * sticky-offset pattern in scrollToComparison (bloodwork-analysis-container).
   */
  private scrollComparisonTitleIntoView = () => {
    // Defer to the next frame so the measurement happens after the table has
    // re-rendered and the browser has settled layout — otherwise a toggle that
    // triggers extra renders (e.g. Hide blank rows dropping whole sections)
    // scrolls to a stale position and lands inconsistently.
    requestAnimationFrame(() => {
      const element = this.comparisonCardRef.current;
      if (!element) return;
      const sticky = document.querySelector(
        '.App-sticky-top',
      ) as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top =
        element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top });
    });
  };

  state: State = {
    showPdfModal: false,
    showUpgradeModal: false,
    numResultsToCompare: NUM_RESULTS_TO_COMPARE,
    headerLogoSrc: undefined,
    showHiddenRows: false, // default: hide empty rows
    withNotes: false,
    showSectionNotes: true, // default: show the Section Notes column
    furtherConsiderations: new FurtherConsiderations(),
    generatingSummary: false,
    aiSummaryDraft: '',
  };

  /**
   * Build the raw-data payload, send it to the AI (Bedrock) via the client
   * endpoint, and drop the returned summary into the "Overall Progress & Trends"
   * field as an editable draft (the practitioner reviews, then Saves).
   */
  onGenerateSummary = async () => {
    const { bloodworks, bloodworkRanges, clientEntryStore } = this.props;
    const { furtherConsiderations } = this.state;
    if (this.state.generatingSummary) return;
    this.setState({ generatingSummary: true });
    try {
      // No overallPractitionerNotes and no supplements: this draft lands in a
      // client-facing report field, so it is built from the marker data alone.
      const payload = buildAIExportData(
        bloodworks,
        bloodworkRanges,
        furtherConsiderations,
      );
      // This lands in a client-facing report field, so use the plain-language,
      // non-diagnostic prompt (not the practitioner one embedded in the export).
      const summary = await clientEntryStore.generateAiSummary({
        instructions: AI_PROGRESS_SUMMARY_PROMPT,
        analysisHistory: payload.analysisHistory,
      });
      this.setState({ aiSummaryDraft: summary || '' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('AI summary generation failed', err);
      const status =
        (err && err.response && err.response.status) ||
        (err && err.message) ||
        'unknown error';
      // eslint-disable-next-line no-alert
      alert(`Could not generate the summary (${status}). Please try again.`);
    } finally {
      this.setState({ generatingSummary: false });
    }
  };

  // ----------- Helper to Hide Rows with No Values ---------
  /** Returns true if this marker has a value in at least one result in the history.
   *  Special-case menstrualPhase to consider altValue as a "value".
   */
  private fieldHasAnyValue(
    field: PanelField,
    history: BloodworkAnalysis[],
  ): boolean {
    return history.some(bwa => {
      const pm = this.findPanelMarker(field, bwa);
      if (!pm) return false;

      if (field.id === 'menstrualPhase') {
        const alt = pm.altValue;

        // Treat undefined, null, or empty/whitespace string as "no value"
        // So menstrual phase behaves like other markers when hide blank rows is ON.
        return alt !== undefined && alt !== null && String(alt).trim() !== '';
      }

      return typeof pm.hasValue === 'function' && pm.hasValue();
    });
  }

  /** Returns true if any field in the section has a value in the entire history */
  private sectionHasAnyValues(
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): boolean {
    return fields.some(f => this.fieldHasAnyValue(f, history));
  }

  // ----------- Section ordering ---------
  /**
   * Section keys in the order they should render — the same order as the
   * test-results analysis page, for both the screen table and the PDF.
   */
  private comparisonSectionOrder(): string[] {
    return [
      'rbc',
      'iron',
      'glucose',
      'lipids',
      'thyroid',
      // Between Thyroid and Vitamin & Mineral Status, matching the analysis
      // page and the PDF packer.
      'digestion',
      'nutrients',
      'proteins',
      'immune',
      'inflammation',
      'platelets',
      'liver',
      'kidney',
      'electrolytes',
      'stressAdrenal',
      'hormones',
      'prostate',
      // Practitioner-typed, per-test markers always come last — they are an
      // appendix to the shipped panels.
      'customMarkers',
    ];
  }

  /**
   * Builds the Hormone Balance section (title + table), honoring "Hide blank rows"
   * and the menstrual-phase inclusion rules. Returns null when it should be hidden.
   */
  private buildHormoneSection(
    history: BloodworkAnalysis[],
    gender: any,
  ): { title: string; table: React.ReactNode } | null {
    // cortisol AM/PM, DHEA-S and pregnenolone moved to the Stress & Adrenal section.
    const hormoneFieldsBase = this.withChartedSectionFields(
      'hormones',
      [
        ...PanelField.HORMONE_FIELDS_1,
        ...PanelField.HORMONE_FIELDS_2,
      ].filter(f => f.id !== 'menstrualPhase' && !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id)),
      history,
    );

    const hideBlanks = !this.state.showHiddenRows;

    const anyHormoneHasValue = hormoneFieldsBase.some(f =>
      this.fieldHasAnyValue(f, history),
    );
    if (!anyHormoneHasValue && hideBlanks) return null;

    const menstrualPhaseField = PanelField.ALL_FIELDS.find(
      f => f.id === 'menstrualPhase',
    );
    // Custom markers filed under Hormones are placed against the hormone
    // fields alone — menstrual phase is prepended afterwards, so a recorded
    // phase with no hormone results can't be what makes the section look
    // populated to the placement guard.
    const hormoneFieldsForTable = [
      ...(menstrualPhaseField ? [menstrualPhaseField] : []),
      ...this.withPlacedCustomFields('hormones', hormoneFieldsBase, history),
    ].filter((f, i, a) => a.findIndex(g => g.id === f.id) === i);

    const table = this.comparisonTable(
      history,
      hormoneFieldsForTable,
      gender,
      this.textMarkersForComparisonKey('hormones', history),
    );
    return table ? { title: 'Hormone Balance', table } : null;
  }

  /**
   * Renders the Hormone Balance section for the PDF, splitting it across
   * multiple pages when the visible markers exceed one page
   * (HORMONE_ROWS_PER_PDF_PAGE). Each spillover page repeats the column header
   * and carries a "Hormone Balance (cont.)" heading + a page break, mirroring
   * the "Insights by Collection Date (cont.)" / MOO "(cont.)" pattern. Without
   * the chunking, the full ~29-marker table is taller than a landscape page and
   * html2pdf slices it mid-row (the reported "cut off onto the next page" bug).
   * Returns null when the section should be hidden.
   *
   * @param history     bloodwork history (one column per collection date)
   * @param gender      client gender; menstrual phase is hidden for males
   * @param breakBefore force a page break before the first chunk
   */
  private renderHormoneSectionPdf(
    history: BloodworkAnalysis[],
    gender: any,
    breakBefore: boolean,
  ): React.ReactNode {
    const hideBlanks = !this.state.showHiddenRows;

    // cortisol AM/PM, DHEA-S and pregnenolone moved to the Stress & Adrenal section.
    const hormoneFieldsBase = this.withChartedSectionFields(
      'hormones',
      [
        ...PanelField.HORMONE_FIELDS_1,
        ...PanelField.HORMONE_FIELDS_2,
      ].filter(f => f.id !== 'menstrualPhase' && !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id)),
      history,
    );

    const anyHormoneHasValue = hormoneFieldsBase.some(f =>
      this.fieldHasAnyValue(f, history),
    );
    if (!anyHormoneHasValue && hideBlanks) return null;

    const menstrualPhaseField = PanelField.ALL_FIELDS.find(
      f => f.id === 'menstrualPhase',
    );
    // Custom markers filed under Hormones are placed against the hormone
    // fields alone — menstrual phase is prepended afterwards, so a recorded
    // phase with no hormone results can't be what makes the section look
    // populated to the placement guard.
    const hormoneFieldsForTable = [
      ...(menstrualPhaseField ? [menstrualPhaseField] : []),
      ...this.withPlacedCustomFields('hormones', hormoneFieldsBase, history),
    ].filter((f, i, a) => a.findIndex(g => g.id === f.id) === i);

    // Mirror comparisonTable's row-visibility filter so the chunk boundaries
    // line up exactly with the rows that will actually render.
    const visibleFields = hormoneFieldsForTable.filter(field => {
      if (field.isHiddenFromComparison) return false;
      if (field.id === 'menstrualPhase' && gender === 'M') return false;
      const hasAny = this.fieldHasAnyValue(field, history);
      return hideBlanks ? hasAny : true;
    });

    // Text markers filed under Hormones. They print on the LAST chunk only:
    // every chunk here is the same section, so resolving them per chunk would
    // repeat the whole set on each page of a long hormone panel — and passing
    // them to none of the chunks (which is what this path did until the rows
    // were added) drops them out of the comparison PDF while the screen shows
    // them. Last rather than first, so the words follow the measurements.
    const hormoneTextMarkers = this.textMarkersForComparisonKey(
      'hormones',
      history,
    );

    if (visibleFields.length === 0 && hormoneTextMarkers.length === 0) {
      return null;
    }

    const chunks: PanelField[][] = [];
    for (
      let i = 0;
      i < visibleFields.length;
      i += HORMONE_ROWS_PER_PDF_PAGE
    ) {
      chunks.push(visibleFields.slice(i, i + HORMONE_ROWS_PER_PDF_PAGE));
    }
    // The loop produces nothing from an empty field list, which would drop a
    // section that is only text results. The placement guard makes that
    // unreachable today (a text marker reaches Hormones only when a hormone
    // field has a value), but a dropped section is a silent failure and one
    // line is cheaper than finding out.
    if (!chunks.length) {
      chunks.push([]);
    }

    return (
      <>
        {chunks.map((chunkFields, chunkIdx) => {
          const table = this.comparisonTable(
            history,
            chunkFields,
            gender,
            chunkIdx === chunks.length - 1 ? hormoneTextMarkers : [],
          );
          if (!table) return null;
          const isCont = chunkIdx > 0;
          return (
            <React.Fragment key={`hormone-chunk-${chunkIdx}`}>
              {(breakBefore || isCont) && (
                <div className="html2pdf__page-break" />
              )}
              <div style={{ height: '8px' }} />
              <h5>
                <strong>Hormone Balance{isCont ? ' (cont.)' : ''}</strong>
              </h5>
              <div
                className="hormone-pdf-section"
                style={{
                  marginBottom: 0,
                  paddingBottom: 10,
                  pageBreakInside: 'avoid',
                  breakInside: 'avoid',
                }}
              >
                <style>
                  {`
                          .hormone-pdf-section td,
                          .hormone-pdf-section th {
                            padding-top: 3px !important;
                            padding-bottom: 3px !important;
                          }
                        `}
                </style>
                {table}
              </div>
            </React.Fragment>
          );
        })}
      </>
    );
  }

  /**
   * Resolves the comparison sections (title + table) in the currently selected
   * order, dropping any section that has no table / is hidden by "Hide blank rows".
   * Shared by the on-screen UI (both orders) and the PDF (analysis order only).
   */
  private buildComparisonSections(
    history: BloodworkAnalysis[],
    gender: any,
    whiteBloodCellsHasValues: boolean,
  ): { key: string; title: string; table: React.ReactNode }[] {
    const hideBlanks = !this.state.showHiddenRows;

    const make = (title: string, table: React.ReactNode | null) =>
      table ? { title, table } : null;

    // Every section's table, built the same way for the screen and the PDF:
    // the two render identical tables, so one helper builds both.
    //
    // Takes the section KEY rather than a finished field list, because a
    // section now needs two lists resolved for it — the charted markers filed
    // into it and the text ones — and passing the key once is the only way the
    // two cannot be resolved for different sections by mistake.
    //
    // The field list comes from the KEY as well, via
    // shippedFieldsForComparisonKey — the same call the PDF height estimate and
    // the custom-marker placement guard make. Passing a list in per section is
    // what let the rendered table and those two drift: a section could list one
    // set of fields on screen and be paginated against another.
    const table = (key: string, sectionGender?: any) =>
      this.comparisonTable(
        history,
        this.withPlacedCustomFields(
          key,
          this.shippedFieldsForComparisonKey(key, history),
          history,
        ),
        sectionGender,
        this.textMarkersForComparisonKey(key, history),
      );

    const sections: {
      [key: string]: { title: string; table: React.ReactNode } | null;
    } = {
      rbc: make(
        'Blood Oxygen & Red Blood Cell Function',
        table('rbc'),
      ),
      iron:
        hideBlanks && !this.anyIronCoreMarkerHasValue(history)
          ? null
          : make(
              'Iron Status & Utilization',
              table('iron'),
            ),
      immune:
        !whiteBloodCellsHasValues && hideBlanks
          ? null
          : make(
              'Immune Activity',
              table('immune'),
            ),
      platelets: make(
        'Blood Clotting & Flow',
        table('platelets'),
      ),
      glucose: make(
        'Glucose Regulation & Metabolic Function',
        table('glucose'),
      ),
      kidney: make(
        'Kidney Function',
        table('kidney'),
      ),
      proteins: make(
        'Protein Status',
        table('proteins'),
      ),
      // Held out of prod on the same flag the analysis page and the PDF use, so
      // the three views of this section appear and disappear together.
      digestion: digestionVisible()
        ? make(
            'Digestive & Gut Health',
            table('digestion'),
          )
        : null,
      nutrients: make(
        'Vitamin & Mineral Status',
        table('nutrients'),
      ),
      electrolytes: make(
        'Electrolyte Balance',
        // Na/K ratio moved to the Stress & Adrenal section.
        table('electrolytes'),
      ),
      lipids: make(
        'Lipid Balance',
        table('lipids'),
      ),
      inflammation: make(
        'Inflammation & Oxidative Stress',
        table('inflammation'),
      ),
      liver: make(
        'Liver Function',
        table('liver'),
      ),
      thyroid: make(
        'Thyroid Function',
        table('thyroid'),
      ),
      stressAdrenal: make(
        'Stress & Adrenal Balance',
        table('stressAdrenal'),
      ),
      hormones: this.buildHormoneSection(history, gender),
      prostate: make(
        'Prostate Function',
        table('prostate'),
      ),
      customMarkers: make(
        Section.CUSTOM_MARKERS,
        // Not through table(): the appendix's field list is not a shipped
        // section's plus placements, it IS the leftovers.
        this.comparisonTable(
          history,
          this.customMarkerComparisonFields(history),
          undefined,
          this.textMarkersForComparisonKey('customMarkers', history),
        ),
      ),
    };

    // The practitioner's own sections, one per name in the history, between the
    // shipped panels and the catch-all appendix — the order the analysis screen
    // and the analysis PDF both use.
    this.practitionerSectionNames(history).forEach(name => {
      const key = practitionerSectionOptionValue(name);
      sections[key] = make(
        practitionerSectionTitle(name),
        this.comparisonTable(
          history,
          this.customFieldsForPractitionerSection(name, history),
          undefined,
          this.textMarkersForComparisonKey(key, history),
        ),
      );
    });

    const order = [
      ...this.comparisonSectionOrder().filter(key => key !== 'customMarkers'),
      ...this.practitionerSectionNames(history).map(
        practitionerSectionOptionValue,
      ),
      'customMarkers',
    ];

    return order
      .map(key => {
        const s = sections[key];
        return s ? { key, title: s.title, table: s.table } : null;
      })
      .filter(Boolean) as {
      key: string;
      title: string;
      table: React.ReactNode;
    }[];
  }

  /**
   * Blood Clotting & Flow comparison field list. Always the coagulation
   * markers; the two viscosity inputs (Hematocrit, then Total Protein) are
   * appended ONLY when the viscosity estimate is present in the history — which
   * requires both inputs in the same test — so a lone HCT/protein row never
   * shows up under coagulation. Shared by the screen table, the PDF, and the
   * PDF height estimate so all three agree.
   */
  /**
   * The PanelField list for the "Other Lab Results" comparison section.
   *
   * Custom markers are per-test, so unlike every other section this list is not
   * a static PanelField array — it is the union of the custom markers across the
   * whole history, in first-appearance order, keyed on the name-derived
   * `custom:` id so the same marker name typed on two tests lines up on one row.
   * The label comes from the MOST RECENT test carrying the key, so a corrected
   * spelling wins.
   *
   * The synthesized fields are the same objects the analysis screen charts with
   * (BloodworkAnalysis.customPanelMarkers), so comparisonTable resolves rows via
   * findPanelMarker and reads the range columns straight off them.
   *
   * Caveat, inherent to per-test ranges: the range columns come from the LAST
   * analysis in the history only (see the Alarm/Lab/Optimal cells in
   * comparisonTable), so a marker absent from the newest test shows blank range
   * columns; and if the practitioner typed different ranges on each test, the
   * trend thumb compares percentFromMean values scored against different range
   * definitions.
   */
  /**
   * The PanelMarker whose range fills a comparison row's six range columns:
   * the newest test that actually has one, searching backwards.
   *
   * Shipped markers resolve a range on every test from the global catalog, so
   * this returns `preferred` (the newest analysis's marker) on the first check.
   * It earns its keep for CUSTOM markers, whose ranges live on the individual
   * test they were typed into — without it, a custom marker entered on an older
   * test and omitted from the newest renders with every range column blank.
   */
  private findRangeBearingPanelMarker(
    field: PanelField,
    history: BloodworkAnalysis[],
    preferred?: PanelMarker,
  ): PanelMarker | undefined {
    if (preferred?.range) {
      return preferred;
    }
    // Scoped to custom markers on purpose. A SHIPPED marker resolves its range
    // from the global catalog on every test, so it only lands here when
    // findRange matched nothing — and an older test can match a different
    // age/menopause/trimester band, which would put a range in these columns
    // that does not belong to the newest test. Leaving those blank is the
    // long-standing behavior and stays untouched.
    if (!CustomMarker.isCustomKey(field.id)) {
      return preferred;
    }
    for (let i = history.length - 1; i >= 0; i--) {
      const panelMarker = this.findPanelMarker(field, history[i]);
      if (panelMarker?.range) {
        return panelMarker;
      }
    }
    return preferred;
  }

  private customMarkerComparisonFields(
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const byId = new Map<string, PanelField>();
    newestFirst(history).forEach(bwa => {
      (bwa.customPanelMarkers || []).forEach(panelMarker => {
        const field = panelMarker.field;
        if (!field || !field.id || byId.has(field.id)) {
          return;
        }
        byId.set(field.id, field);
      });
    });
    // Markers filed under a body system are shown there instead — but only when
    // that section has results of its own somewhere in the history, mirroring
    // BloodworkAnalysis.withCustomMarkers()'s empty-target guard. Otherwise they
    // come back here, so a placed marker is never lost between the two.
    //
    // A marker filed under a section the practitioner NAMED is shown there
    // unconditionally: that section exists because they made it, so there is no
    // empty target to guard against.
    return Array.from(byId.values()).filter(
      field =>
        !this.isPlacedInSection(field, history) && !field.practitionerSection,
    );
  }

  /**
   * The practitioner-named sections present anywhere in the history, in
   * first-appearance order, each carrying the name as it was typed.
   *
   * Deduplicated on the normalized name so one panel typed "IgG Allergens" in
   * March and "IGG allergens" in June is one section with one set of rows,
   * rather than two sections that each look half-empty.
   */
  private practitionerSectionNames(history: BloodworkAnalysis[]): string[] {
    const byKey = new Map<string, string>();
    history.forEach(bwa => {
      (bwa.customPanelMarkers || []).forEach(panelMarker => {
        const name = (panelMarker.field?.practitionerSection || '').trim();
        if (!name) {
          return;
        }
        const key = practitionerSectionKey(name);
        // Later tests win the spelling, the first one wins the position.
        byKey.set(key, name);
      });
      // Charted markers first, then the text ones — a section can be made
      // entirely of text results (a urinalysis panel is), and without this it
      // would have no heading in the comparison report while having one on the
      // analysis screen and in the PDF.
      (bwa.customTextMarkers || []).forEach(textMarker => {
        const name = (textMarker.practitionerSection || '').trim();
        if (!name || !textMarker.hasText()) {
          return;
        }
        byKey.set(practitionerSectionKey(name), name);
      });
    });
    return Array.from(byKey.values());
  }

  /** The comparison fields for one practitioner-named section. */
  private customFieldsForPractitionerSection(
    name: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const key = practitionerSectionKey(name);
    const byId = new Map<string, PanelField>();
    newestFirst(history).forEach(bwa => {
      (bwa.customPanelMarkers || []).forEach(panelMarker => {
        const field = panelMarker.field;
        if (
          field &&
          field.id &&
          practitionerSectionKey(field.practitionerSection) === key &&
          !byId.has(field.id)
        ) {
          byId.set(field.id, field);
        }
      });
    });
    return Array.from(byId.values());
  }

  /**
   * Every custom marker in the history filed under `section`, deduplicated the
   * same way customMarkerComparisonFields deduplicates: by the name-derived
   * `custom:` id, newest label winning.
   */
  private customFieldsForSection(
    section: Section,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const byId = new Map<string, PanelField>();
    newestFirst(history).forEach(bwa => {
      (bwa.customPanelMarkers || []).forEach(panelMarker => {
        const field = panelMarker.field;
        if (
          field &&
          field.id &&
          field.customSection === section &&
          !byId.has(field.id)
        ) {
          byId.set(field.id, field);
        }
      });
    });
    return Array.from(byId.values());
  }

  /**
   * Every text-result marker anywhere in the history, deduplicated by name.
   *
   * The text counterpart of customMarkerComparisonFields, and deduplicated the
   * same way: keyed on the name-derived `custom:` id, so a urinalysis typed on
   * three tests is one row with three cells rather than three rows. Later tests
   * win the label and the expectation (a corrected spelling should show, and
   * the newest expectation is the one the practitioner currently holds), the
   * first appearance wins the position.
   *
   * Read from each analysis's OWN markers — hasText() filters out the
   * previous-only stand-ins it built for the analysis screen, which here would
   * be the previous test's results counted twice.
   */
  private textMarkersInHistory(
    history: BloodworkAnalysis[],
  ): CustomTextMarker[] {
    const byKey = new Map<string, CustomTextMarker>();
    newestFirst(history).forEach(bwa => {
      (bwa.customTextMarkers || []).forEach(textMarker => {
        // First seen wins outright, and newest-first means that is the newest
        // test carrying it: its position, its wording, its section. The older
        // tests' copies of the same marker say nothing this one does not.
        if (!textMarker.hasText() || byKey.has(textMarker.key)) {
          return;
        }
        // The per-test words are read separately (textValueFor), so this
        // carries the marker's identity only.
        byKey.set(
          textMarker.key,
          new CustomTextMarker({
            ...textMarker,
            text: undefined,
            previousText: undefined,
          }),
        );
      });
    });
    return Array.from(byKey.values());
  }

  /** One test's words for a text marker, or '' when it did not carry it. */
  private textValueFor(
    textMarker: CustomTextMarker,
    bwa: BloodworkAnalysis,
  ): string {
    const found = (bwa.customTextMarkers || []).find(
      entry => entry.key === textMarker.key && entry.hasText(),
    );
    return found ? found.text!.trim() : '';
  }

  /**
   * Whether a text marker's chosen shipped section has results of its own
   * anywhere in the history — the same empty-target guard isPlacedInSection
   * applies to charted markers, asked of the same section field.
   */
  private isTextPlacedInSection(
    textMarker: CustomTextMarker,
    history: BloodworkAnalysis[],
  ): boolean {
    if (!textMarker.section) {
      return false;
    }
    const key = Object.keys(COMPARISON_SECTION_BY_KEY).find(
      k => COMPARISON_SECTION_BY_KEY[k] === textMarker.section,
    );
    if (!key) {
      return false;
    }
    return this.shippedFieldsForComparisonKey(key, history).some(shipped =>
      this.fieldHasAnyValue(shipped, history),
    );
  }

  /**
   * The text markers belonging to one comparison section key.
   *
   * Mirrors shippedFieldsForComparisonKey's three-way split so a marker lands
   * in exactly one section here, in the section the analysis screen and the PDF
   * put it in.
   */
  private textMarkersForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): CustomTextMarker[] {
    const all = this.textMarkersInHistory(history);
    const practitionerName = practitionerSectionFromOptionValue(key);
    if (practitionerName) {
      const wanted = practitionerSectionKey(practitionerName);
      return all.filter(
        textMarker =>
          !this.isTextPlacedInSection(textMarker, history) &&
          practitionerSectionKey(textMarker.practitionerSection) === wanted,
      );
    }
    if (key === 'customMarkers') {
      return all.filter(
        textMarker =>
          !this.isTextPlacedInSection(textMarker, history) &&
          !textMarker.practitionerSection,
      );
    }
    const section = COMPARISON_SECTION_BY_KEY[key];
    if (!section) {
      return [];
    }
    return all.filter(
      textMarker =>
        textMarker.section === section &&
        this.isTextPlacedInSection(textMarker, history),
    );
  }

  /** Whether a custom field's chosen section has results of its own to sit with. */
  private isPlacedInSection(
    field: PanelField,
    history: BloodworkAnalysis[],
  ): boolean {
    if (!field.customSection) {
      return false;
    }
    const key = Object.keys(COMPARISON_SECTION_BY_KEY).find(
      k => COMPARISON_SECTION_BY_KEY[k] === field.customSection,
    );
    if (!key) {
      return false;
    }
    return this.shippedFieldsForComparisonKey(key, history).some(shipped =>
      this.fieldHasAnyValue(shipped, history),
    );
  }

  /**
   * A section's own comparison fields, plus any custom markers filed under it.
   *
   * Wrapped around every field list the comparison renders AND the list its PDF
   * height estimate counts, so the table, the page-break decision and the
   * analysis page all agree on where a custom marker lives.
   */
  private withPlacedCustomFields(
    key: string,
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const section = COMPARISON_SECTION_BY_KEY[key];
    if (!section) {
      return fields;
    }
    // Empty-target guard, same as the analysis page: a section with nothing of
    // its own is not brought to life by a custom marker.
    if (!fields.some(field => this.fieldHasAnyValue(field, history))) {
      return fields;
    }
    const placed = this.customFieldsForSection(section, history);
    return placed.length ? [...fields, ...placed] : fields;
  }

  private plateletsComparisonFields(
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const viscosity = PanelField.findField(
      PanelField.PLATELETS_FIELDS,
      'viscosityCapiliaries',
    );
    const showInputs =
      !!viscosity && this.fieldHasAnyValue(viscosity, history);
    return PanelField.plateletsComparisonFields(showInputs);
  }

  /**
   * The PanelField list backing each comparison section key, mirroring the
   * field sets used in buildComparisonSections. Used to estimate a section's
   * rendered height (via countVisibleFields) so the PDF renderer can decide
   * whether a section is tall enough to need its own fresh page.
   */
  private fieldsForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    return this.withPlacedCustomFields(
      key,
      this.shippedFieldsForComparisonKey(key, history),
      history,
    );
  }

  /**
   * The section's own fields, before any practitioner-added markers are placed
   * into it. Kept separate so the placement guard has something to ask "does
   * this section have results of its own?" without recursing.
   *
   * = the section's declared field list PLUS anything the analysis page
   * actually charts under it that the list doesn't name (see
   * chartedFieldsForComparisonKey). A marker on the report and not in the
   * comparison table is the failure this exists to make impossible.
   */
  private shippedFieldsForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    return this.withChartedSectionFields(
      key,
      this.declaredFieldsForComparisonKey(key, history),
      history,
    );
  }

  /**
   * `fields` plus anything the analysis page charts under this section that
   * they don't already name. Separate from shippedFieldsForComparisonKey so the
   * Hormone Balance section — which builds its own list twice, for the screen
   * and for the PDF's page chunking — can top itself up the same way.
   */
  private withChartedSectionFields(
    key: string,
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const present = new Set(fields.map(field => field.id));
    const charted = this.chartedFieldsForComparisonKey(key, history).filter(
      field => !present.has(field.id),
    );
    return charted.length ? [...fields, ...charted] : fields;
  }

  /**
   * Fields the ANALYSIS page charts under this section somewhere in the
   * history, in the order its panel getter returns them.
   *
   * Sections borrow markers from each other — the Immune panel charts zinc, the
   * Inflammation panel charts ferritin/albumin/GGT and eight more, and Glucose
   * pulls in LDL, ALT, cortisol and the rest of its add-ons when a pattern
   * flags them. Those markers were on the analysis report and in the client's
   * PDF, but the comparison table listed only each section's own declared
   * fields, so a practitioner comparing two tests lost them from the section
   * they had just read them in. (They still appeared under their home section,
   * which is why this read as an ordering quirk rather than missing data.)
   *
   * Derived from the getter rather than a hand-written second list, so a marker
   * added to a section's panel reaches the comparison table without a matching
   * edit here — and so the conditional add-ons appear exactly when the analysis
   * page shows them, not on every test that happens to carry an LDL.
   *
   * Practitioner-typed markers are excluded: withPlacedCustomFields already
   * places those, and it applies an empty-target guard this must not bypass.
   */
  private chartedFieldsForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const section = COMPARISON_SECTION_BY_KEY[key];
    if (!section) {
      return [];
    }
    const getterName = SECTION_PANEL_MARKER_GETTERS[section];
    if (!getterName) {
      return [];
    }
    const seen = new Set<string>();
    const fields: PanelField[] = [];
    history.forEach(bwa => {
      const getter = (bwa as any)[getterName];
      if (typeof getter !== 'function') {
        return;
      }
      (getter.call(bwa) || []).forEach((panelMarker: PanelMarker) => {
        const field = panelMarker && panelMarker.field;
        if (!field || CustomMarker.isCustomKey(panelMarker.attribute)) {
          return;
        }
        if (seen.has(field.id)) {
          return;
        }
        seen.add(field.id);
        fields.push(field);
      });
    });
    return fields;
  }

  /**
   * The field list each section DECLARES — the static arrays the sections were
   * built from. shippedFieldsForComparisonKey tops this up with whatever the
   * analysis page charts beyond it.
   */
  private declaredFieldsForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    switch (key) {
      case 'rbc':
        return [...PanelField.RBC_FIELDS];
      case 'iron':
        return [...PanelField.IRON_FIELDS, ...PanelField.COPPER_FIELDS];
      case 'immune':
        return [
          ...PanelField.IMMUNE_SYSTEM_FIELDS,
          ...PanelField.WBC_ABSOLUTE_DIFFERENTIALS,
        ];
      case 'platelets':
        return this.plateletsComparisonFields(history);
      case 'glucose':
        return [
          ...PanelField.GLUCOSE_FIELDS,
          ...PanelField.GLUCOSE_TG_HDL_SECTION_FIELDS,
        ];
      case 'kidney':
        return [...PanelField.KIDNEY_FIELDS];
      case 'proteins':
        return [...PanelField.PROTEINS_FIELDS];
      case 'digestion':
        // Resolved from ALL_FIELDS rather than a PanelField array of its own:
        // every digestion marker is borrowed from another panel, so the ids are
        // the list and the field definitions stay where they already live.
        // Order comes from DIGESTION_FIELD_IDS, which prints the same order the
        // analysis page and the PDF do.
        return DIGESTION_FIELD_IDS.map(id =>
          PanelField.findField(PanelField.ALL_FIELDS, id),
        ).filter(Boolean) as PanelField[];
      case 'nutrients':
        return [...PanelField.NUTRIENTS_FIELDS, ...PanelField.ZINC_FIELDS];
      case 'electrolytes':
        return PanelField.ELECTROLYTES_FIELDS.filter(f => f.id !== 'sodiumPotassiumRatio');
      case 'stressAdrenal':
        return [...PanelField.STRESS_ADRENAL_FIELDS];
      case 'lipids':
        return [...PanelField.COMPARISON_LIPIDS_FIELDS];
      case 'inflammation':
        return [...PanelField.COMPARISON_INFLAMMATION_FIELDS];
      case 'liver':
        return [...PanelField.LIVER_FIELDS];
      case 'thyroid':
        return [...PanelField.THYROID_FIELDS];
      case 'hormones':
        return [...PanelField.HORMONE_FIELDS_1, ...PanelField.HORMONE_FIELDS_2].filter(
          f => !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id),
        );
      case 'prostate':
        return [...PanelField.PROSTATE_FIELDS];
      case 'customMarkers':
        return this.customMarkerComparisonFields(history);
      default: {
        // The practitioner's own sections are keyed by name rather than by a
        // fixed key, since there is no fixed list of them.
        const name = practitionerSectionFromOptionValue(key);
        return name ? this.customFieldsForPractitionerSection(name, history) : [];
      }
    }
  }

  /**
   * Renders the PDF sections in the test-results analysis order — the one and
   * only comparison order (the old lab-report order was retired).
   *
   * Page-break strategy (works WITH html2pdf 0.9.1's actual engine, not against
   * it — see node_modules/html2pdf.js/src/plugin/pagebreaks.js):
   *
   *  1. Every section's title + table is wrapped in a `.pdf-section` /
   *     `.section-block` pair carrying `page-break-inside: avoid`. For any
   *     section that fits within a single page, html2pdf moves the WHOLE block
   *     to the next page when it would otherwise straddle a boundary — so the
   *     title can never be stranded at the bottom of a page with its table on
   *     the next (the "Kidney Function" orphan bug). `break-after/before: avoid`
   *     would be cleaner but html2pdf silently ignores them; `inside: avoid` is
   *     the only keep-together primitive it honors.
   *
   *  2. html2pdf only applies that whole-block push when the block is <= 1 page
   *     tall; a taller section is left to break naturally, which could again
   *     orphan its title if it happens to start near a page bottom. To preserve
   *     the no-orphan guarantee for EVERY combination of sections, any section
   *     whose visible-row count could approach/exceed one page is forced onto a
   *     fresh page (title lands at the top; only the table body — never the
   *     title — spills across the page boundary).
   */
  private renderPdfSectionsAnalysisOrder(
    history: BloodworkAnalysis[],
    gender: any,
    whiteBloodCellsHasValues: boolean,
  ): React.ReactNode {
    // Sections that always begin on a fresh page (heavy and/or grouped this way
    // historically). Glucose has long been a hard page-start in this report.
    // customMarkers is always last and its row count is unbounded (up to 20 per
    // test), so it can't be safely packed against a neighbour either.
    const alwaysBreakBefore = new Set([
      'glucose',
      'hormones',
      'prostate',
      'customMarkers',
    ]);

    // A section with this many visible marker rows or more is treated as "tall"
    // (could fill or exceed a landscape page) and is started on its own page so
    // its title is guaranteed to land at the top, never orphaned. Kept safely
    // below the ~14–16 rows a landscape page holds, and consistent with the
    // retired lab-report layout's "Nutrients > 10 → own page" tuning.
    const TALL_SECTION_ROW_THRESHOLD = 12;

    const sections = this.buildComparisonSections(
      history,
      gender,
      whiteBloodCellsHasValues,
    );
    // Precompute each section's height class and whether it opens a fresh page,
    // so the "lead the page" rule below can run before we render.
    const meta = sections.map((section, idx) => {
      const visibleRows =
        this.countVisibleFields(
          this.fieldsForComparisonKey(section.key, history),
          history,
        ) +
        // Text markers render as rows like any other, so a section of fourteen
        // urinalysis lines is exactly as tall as one of fourteen charted
        // markers — and has to be measured that way, or it overruns the page
        // this estimate just decided it fits on.
        this.textMarkersForComparisonKey(section.key, history).length;
      const isTall = visibleRows >= TALL_SECTION_ROW_THRESHOLD;
      const forcesFreshPage =
        idx > 0 && (alwaysBreakBefore.has(section.key) || isTall);
      return { section, isTall, forcesFreshPage };
    });

    // Whether each section emits a page break before it. Seeded from
    // forcesFreshPage, then adjusted by the lead-the-page rule below.
    const startsFreshPage = meta.map(m => m.forcesFreshPage);

    // Lead-the-page rule: a SHORT section sandwiched between a tall section
    // (which fills/spills its own page) and a following tall section (which
    // would force its own fresh page) gets stranded alone — html2pdf bumps the
    // short block off the spill page via page-break-inside:avoid, and the next
    // tall section then opens a brand-new page below it. This is the reported
    // "Protein Status alone on a page, Immune Activity on the next page" bug.
    // Move the break to BEFORE the short section so it LEADS the fresh page the
    // tall section was going to open, and let the tall section flow directly
    // beneath it (Protein + Immune share a page; everything after moves up).
    // Only applies when the next section forces a page purely by height — never
    // the intentional hard-break sections (Glucose/Hormones/Prostate).
    for (let i = 1; i < meta.length - 1; i++) {
      const prev = meta[i - 1];
      const cur = meta[i];
      const next = meta[i + 1];
      const nextForcedByHeight =
        next.forcesFreshPage && !alwaysBreakBefore.has(next.section.key);
      if (!cur.forcesFreshPage && prev.isTall && nextForcedByHeight) {
        startsFreshPage[i] = true; // short section leads the fresh page
        startsFreshPage[i + 1] = false; // tall section flows beneath it
      }
    }

    return (
      <>
        {meta.map(({ section, isTall }, idx) => {
          const needsFreshPage = startsFreshPage[idx];
          // Hormone Balance has ~29 possible markers and can run several rows
          // past a single page. Render it as page-sized chunks, each with its
          // own column header and a repeated "Hormone Balance (cont.)" heading,
          // instead of letting one oversized table flow (and get sliced
          // mid-row) across the page boundary.
          if (section.key === 'hormones') {
            return (
              <React.Fragment key={section.key}>
                {this.renderHormoneSectionPdf(history, gender, needsFreshPage)}
              </React.Fragment>
            );
          }
          // A section whose title + table is taller than one page cannot be
          // kept together. With the default page-break-inside:avoid on
          // .section-block, html2pdf bumps the whole table to the next page when
          // it would straddle the boundary — which strands the title alone on
          // the forced-fresh page above it. For these tall sections, let the
          // table flow so the title stays glued to its first rows; individual
          // rows stay intact via the `tr` keep-together rule in the style block.
          const flowStyle: React.CSSProperties | undefined = isTall
            ? { pageBreakInside: 'auto', breakInside: 'auto' }
            : undefined;
          return (
            <React.Fragment key={section.key}>
              {needsFreshPage && <div className="html2pdf__page-break" />}
              <div className="pdf-section" style={flowStyle}>
                <div style={{ height: '8px' }} />
                <h5>
                  <strong>{section.title}</strong>
                </h5>
                <div className="section-block" style={flowStyle}>
                  {section.table}
                </div>
                <div style={{ height: '8px' }} />
              </div>
            </React.Fragment>
          );
        })}
      </>
    );
  }

  /** Count how many fields would render given the current hide/show blanks setting.
   *  - When showHiddenRows === false (Hide blank rows is CHECKED):
   *      Only fields with at least one value in the history are counted.
   *  - When showHiddenRows === true (Hide blank rows is UNCHECKED):
   *      All non-hidden fields are counted, even if they have no values.
   */
  private countVisibleFields(
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): number {
    const hideBlanks = !this.state.showHiddenRows;
    const visible = fields.filter(
      f =>
        !f.isHiddenFromComparison &&
        (hideBlanks ? this.fieldHasAnyValue(f, history) : true),
    );
    return visible.length;
  }

  /** Returns true if any of the iron markers in PanelField.IRON_FIELDS
   *  has a value anywhere in the history.
   *
   *  Used for the "hide blank rows" rule:
   *  - When hide blank rows is ON:
   *      If none of these iron markers has a value, the entire
   *      "Iron Related Markers" section is hidden (UI + PDF).
   *  - When hide blank rows is OFF:
   *      The section is always shown, even if all values are blank.
   */
  private anyIronCoreMarkerHasValue(history: BloodworkAnalysis[]): boolean {
    const ironOnlyFields = [...PanelField.IRON_FIELDS];
    return this.sectionHasAnyValues(ironOnlyFields, history);
  }

  // ---------- Logo helpers (preview reliability) ----------

  getLogoUrl = (): string => {
    const { currentUserProfile } = this.props;

    return (
      currentUserProfile?.hasCustomLogo()
        ? currentUserProfile.customLogoUrl
        : (logo as any)
    ) as any;
  };

  /** Normalize whatever we store (URL, key, filename) into an S3 key if possible. */
  private extractUserLogoKey = (candidate: string): string | undefined => {
    if (!candidate) return undefined;

    // Already a key like "user-logos/abc.png"
    if (/^user-logos\//i.test(candidate)) {
      return candidate.replace(/^\/+/, '');
    }

    // "public/user-logos/..." (Amplify storage path)
    const publicIdx = candidate.indexOf('public/user-logos/');
    if (publicIdx >= 0) {
      return candidate.slice(publicIdx + 'public/'.length);
    }

    // Any URL that contains "/user-logos/..."
    const seg = '/user-logos/';
    const idx = candidate.indexOf(seg);
    if (idx >= 0) {
      return candidate.slice(idx + 1); // drop the leading '/'
    }

    // Bare filename? assume user-logos/<filename>
    const isUrl = /^https?:\/\//i.test(candidate) || /^data:/i.test(candidate);
    if (!isUrl) {
      const fn = candidate.split('/').pop();
      if (fn) return `user-logos/${fn}`;
    }

    return undefined;
  };

  /** Convert any URL to a data URL for reliable rendering */
  private toDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url, { credentials: 'omit' });
    const blob = await res.blob();
    return await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  /** Ensure the preview <Image id="logo"> gets a real, loadable data URL. */
  private ensureHeaderPreviewLogo = async () => {
    const { currentUserProfile } = this.props;

    // Fallback bundled asset (always resolvable)
    const bundled =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    // No custom logo: use bundled default directly, skip all S3 logic
    if (!currentUserProfile?.hasCustomLogo()) {
      try {
        const dataUrl = await this.toDataUrl(bundled);
        this.setState({ headerLogoSrc: dataUrl });
      } catch {
        this.setState({ headerLogoSrc: bundled });
      }
      return;
    }

    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    // If full URL, convert to data URL
    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        const dataUrl = await this.toDataUrl(candidate);
        this.setState({ headerLogoSrc: dataUrl });
        return;
      } catch {
        // fall through to S3/bundled
      }
    }

    // If already a data URL, use it directly
    if (candidate && /^data:/i.test(candidate)) {
      this.setState({ headerLogoSrc: candidate });
      return;
    }

    // Try to sign via Storage.get and convert to data URL
    try {
      const key = this.extractUserLogoKey(candidate);
      if (key) {
        const signed = (await Storage.get(key)) as string;
        const dataUrl = await this.toDataUrl(signed);
        this.setState({ headerLogoSrc: dataUrl });
        return;
      }
    } catch {
      // fall through
    }

    // Fallback to bundled (convert to data URL)
    try {
      const dataUrl = await this.toDataUrl(bundled);
      this.setState({ headerLogoSrc: dataUrl });
    } catch {
      this.setState({ headerLogoSrc: bundled });
    }
  };

  // -------------------------------------------------------

  componentDidMount() {
    // Make sure modal preview shows a loadable logo URL
    this.ensureHeaderPreviewLogo();

    // Seed the Section Notes column from the practitioner's last choice. It
    // lives in localStorage rather than the profile so it needs no schema
    // change; unset means the default (shown).
    const key = this.sectionNotesStorageKey();
    if (key && localStorage.getItem(key) === 'false') {
      this.setState({ showSectionNotes: false });
    }

    this.props.furtherConsiderationsEntryStore
      ?.loadFurtherConsiderations()
      .then(furtherConsiderations => {
        this.setState({ furtherConsiderations });
      });
  }

  /** Per-user key for the Section Notes column preference. */
  private sectionNotesStorageKey(): string | null {
    const id = this.props.currentUserProfile?.id;
    return id ? `${id}-comparison-show-section-notes` : null;
  }

  /** Show/hide the Section Notes column (applies to the PDF export too). */
  private handleShowSectionNotesChange = (checked: boolean) => {
    this.setState({ showSectionNotes: checked });
    const key = this.sectionNotesStorageKey();
    if (key) localStorage.setItem(key, String(checked));
  };

  findPanelMarker(
    panelField: PanelField,
    bloodworkAnalysis: BloodworkAnalysis,
  ): PanelMarker {
    return bloodworkAnalysis.findPanelMarker(panelField.id);
  }

  displayValue(panelMarker: PanelMarker) {
    if (panelMarker) {
      return panelMarker.hasValue()
        ? MathUtils.round(panelMarker.value, panelMarker.field.precision)
        : null;
    } else {
      return 'N/A';
    }
  }

  getClassName(pm: PanelMarker): string {
    if (!pm || pm.isBlank() || pm.isOptimal()) {
      return '';
    }

    if (pm.field.noRedChart) {
      if (pm.isNotOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (!pm.isLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else if (pm.field.noRedOrangeChart) {
      if (pm.isNotOptimal() && pm.isLessThanOrIsLab()) {
        return 'bg-yellow';
      } else if (pm.isGreaterThanLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else if (pm.field.useRDWChart) {
      if (pm.isGreaterThanOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (pm.isGreaterThanLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else {
      if (pm.isNotOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (pm.isNotLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (!pm.isAlarm()) {
        return 'bg-red';
      }
    }
    return '';
  }

  getIcon(pm: PanelMarker) {
    if (pm.isBlank()) return null;
    let img: any;
    if (pm.isGreaterThanOptimal()) {
      img = arrow_up;
    } else if (pm.isStricklyLessThanOptimal()) {
      img = arrow_down;
    }
    return <img src={img} alt="" />;
  }

  comparisonDetails(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    pm: PanelMarker,
    index: number,
  ) {
    if (!pm) {
      return null;
    }

    if (pm.field.id === 'menstrualPhase') {
      return (
        <td>
          {pm.altValue === MenstrualPhase.NONE ? (
            <>
              {MenstrualPhase.NONE}
              <br />
              (children&apos;s ranges)
            </>
          ) : (
            pm.altValue
          )}
        </td>
      );
    }

    let img, imgSrc;
    const valueDetail = (
      <td className={this.getClassName(pm)} key={index}>
        {this.displayValue(pm)}&nbsp;&nbsp;{this.getIcon(pm)}
      </td>
    );
    if (index + 1 < bloodworkAnalysisHistory.length) {
      return valueDetail;
    } else {
      if (
        pm &&
        pm.hasValue() &&
        pm.previousPanelMarker &&
        pm.previousPanelMarker.hasValue()
      ) {
        if (pm.isOptimal() && pm.previousPanelMarker.isOptimal()) {
          // No op
        } else {
          if (
            Math.abs(pm.percentFromMean) <
            Math.abs(pm.previousPanelMarker.percentFromMean)
          ) {
            imgSrc = thumbs_up;
          } else if (
            Math.abs(pm.percentFromMean) >
            Math.abs(pm.previousPanelMarker.percentFromMean)
          ) {
            imgSrc = thumbs_down;
          }
          img = <img src={imgSrc} alt="" />;
        }
      }

      return (
        <>
          {valueDetail}
          <td style={{ textAlign: 'center' }}>{img}</td>
          <td>{pm.range?.alarmMin}</td>
          <td>{pm.range?.labMin}</td>
          <td>{pm.range?.optimalMin}</td>
          <td>{pm.range?.optimalMax}</td>
          <td>{pm.range?.labMax}</td>
          <td>{pm.range?.alarmMax}</td>
        </>
      );
    }
  }

  exportTooltip() {
    return (
      <Tooltip id="export-tooltip">
        <div>Click this icon to export to PDF</div>
      </Tooltip>
    );
  }

  exportJsonTooltip() {
    return (
      <Tooltip id="export-json-tooltip">
        <div>Download all of this client&apos;s data — every test, plus notes and analyses — as a file to use with an AI</div>
      </Tooltip>
    );
  }

  /** Click popover with a prompt users can copy to use with the exported file. */
  exportPromptPopover() {
    return (
      <Popover id="export-prompt-popover" className="export-prompt-popover">
        <Popover.Title
          as="div"
          style={{ fontWeight: 600, backgroundColor: '#fff', borderBottom: 'none' }}
        >
          Using this file with an AI
        </Popover.Title>
        <Popover.Content>
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '6px' }}>
              Download the file, then upload it to an AI assistant (Claude, ChatGPT,
              Gemini, etc.) with this prompt:
            </div>
            <div style={{ fontStyle: 'italic' }}>&ldquo;{AI_FILE_PROMPT}&rdquo;</div>
            <CopyPromptButton text={AI_FILE_PROMPT} />
            <div style={{ marginTop: '8px', opacity: 0.85 }}>
              (The file also includes full instructions at the top.)
            </div>
          </div>
        </Popover.Content>
      </Popover>
    );
  }

  exportInfoTooltip() {
    return (
      <Tooltip id="export-info-tooltip">
        <div>
          Click this icon to learn more about the export to PDF feature.
        </div>
      </Tooltip>
    );
  }

  openExportInfoWindow = () => {
    window.open('https://labsmarts.com/exporttopdf');
  };

  exportWithNotesTooltip() {
    return (
      <Tooltip id="export-with-notes-tooltip">
        <div>Click this icon to export to PDF with notes</div>
      </Tooltip>
    );
  }

  hideBlankRowsTooltip() {
    return (
      <Tooltip id="hide-blank-rows-tooltip">
        <div>
          When checked, marker rows with no recorded value across every
          collection are hidden, and sections with no values drop out entirely.
          Uncheck to show all markers, including blanks.
        </div>
      </Tooltip>
    );
  }

  showSectionNotesTooltip() {
    return (
      <Tooltip id="show-section-notes-tooltip">
        <div>
          Shows each test's Section Notes (the notes typed on the individual
          section tabs) in their own column beside the Insights note, which
          summarises the whole test. Applies to the PDF export as well. Edit on
          a row changes both, and what you type in Section Notes is kept on that
          test until you reset it.
        </div>
      </Tooltip>
    );
  }

  /** A thin vertical rule used to separate groups in the card header. */
  headerDivider() {
    return (
      <span
        aria-hidden="true"
        style={{
          width: '1px',
          height: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          alignSelf: 'center',
        }}
      />
    );
  }

  export(withNotes = false) {
    this.props.onExportToPdf();
    if (this.props.currentUserProfile.pdfExportComparisonTableAllowed) {
      this.setState({
        showPdfModal: true,
        numResultsToCompare: NUM_RESULTS_TO_COMPARE_FOR_PDF,
        withNotes,
      });
    } else {
      this.setState({
        showUpgradeModal: true,
      });
    }
  }

  exportJson = () => {
    const { bloodworks, bloodworkRanges, clientEntryStore, currentUserProfile } =
      this.props;
    const { furtherConsiderations } = this.state;

    // "Overall Practitioner Notes" is a client-level field — read it from the LIVE
    // client (store), NOT the per-test snapshot, so a practitioner edit/delete is
    // reflected. All tests share one clientId, so bloodworks[0] is sufficient.
    const clientId = bloodworks && bloodworks[0] ? bloodworks[0].clientId : undefined;
    const liveClient =
      clientEntryStore?.clients?.find(c => c.id === clientId) ||
      (bloodworks && bloodworks[0] ? bloodworks[0].client : undefined);

    // The practitioner's curated Recommended Supplements list rides along with
    // the DOWNLOAD only. onGenerateSummary() calls the same builder without
    // this, because that draft prints on the client's report and product names
    // must not be able to reach it.
    const exportData = buildAIExportData(
      bloodworks,
      bloodworkRanges,
      furtherConsiderations,
      liveClient?.overallPractitionerNotes,
      {
        includeRecommendedSupplements: true,
        enabledSupplementBrands: currentUserProfile?.enabledSupplementBrands,
      },
    );

    const jsonString = JSON.stringify(exportData, null, 2);
    // Declare UTF-8 so consumers don't fall back to Latin-1/CP1252 and mojibake
    // the µ in units (µmol/L, etc.). Direction glyphs are already spelled out.
    const blob = new Blob([jsonString], {
      type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    // Name the file with the client's initials + the latest collection date, so
    // the practitioner can recognize the download. This is only for their local
    // use — the file is fed to Bedrock (under the AWS BAA) and the AI receives
    // the JSON contents, not the filename.
    const client = bloodworks && bloodworks[0] ? bloodworks[0].client : undefined;
    const initials = client
      ? `${(client.firstName || '').charAt(0)}${(client.lastName || '').charAt(0)}`
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '')
      : '';
    const collectionDates = (bloodworks || [])
      .map(bw => bw && bw.collectionDate)
      .filter(Boolean) as string[];
    const latestCollectionDate = collectionDates.length
      ? collectionDates.reduce((a, b) => (a > b ? a : b))
      : undefined;
    const datePart = moment(latestCollectionDate).format('YYYY-MM-DD');
    const filename = initials
      ? `blood_work_raw_data_${initials}_${datePart}.json`
      : `blood_work_raw_data_${datePart}.json`;

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  comparisonTable(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    panelFields: PanelField[],
    clientGender?: string, //For menstrual phase - not showing if gender is male
    /**
     * This section's practitioner-typed markers whose result is words.
     *
     * Rendered as rows beneath the charted ones, in the same table, for the
     * same reason the analysis screen puts them in the same panel: a section is
     * one table however its markers happen to be reported.
     */
    textMarkers: CustomTextMarker[] = [],
  ) {
    // Determine which rows to show
    const hideBlanks = !this.state.showHiddenRows; // checkbox checked = hide blanks

    const visibleFields = panelFields.filter((field: PanelField) => {
      if (field.isHiddenFromComparison) return false;

      // Never show menstrual phase for male clients
      if (field.id === 'menstrualPhase' && clientGender === 'M') {
        return false;
      }

      const hasAny = this.fieldHasAnyValue(field, bloodworkAnalysisHistory);
      // When hideBlanks is true: only fields with data (incl. menstrualPhase via altValue).
      // When hideBlanks is false: show all fields (incl. menstrualPhase) if not male.
      return hideBlanks ? hasAny : true; // default hide; show all when unchecked
    });

    // If nothing to show, don't render a table at all. A section can be made
    // ENTIRELY of text markers — a urinalysis panel is exactly that — so an
    // empty field list is no longer an empty section.
    if (visibleFields.length === 0 && textMarkers.length === 0) {
      return null;
    }

    return (
      <Table striped bordered hover className="labsmartz-table">
        <thead>
          <tr>
            <th>Marker</th>
            <th>Units</th>
            {bloodworkAnalysisHistory.map(
              (bloodworkAnalysis: BloodworkAnalysis, index: number) => {
                return (
                  <th key={index}>
                    <Moment format="MM/DD/YYYY">
                      {bloodworkAnalysis.bloodwork.collectionDate}
                    </Moment>
                  </th>
                );
              },
            )}
            <th>Trend</th>
            <th>Alarm Min</th>
            <th>Lab Min</th>
            <th>Optimal Min</th>
            <th>Optimal Max</th>
            <th>Lab Max</th>
            <th>Alarm Max</th>
          </tr>
        </thead>

        <tbody>
          {visibleFields.map((field: PanelField, rowIdx: number) => {
            const valueCells = bloodworkAnalysisHistory.map((bwa, colIdx) => {
              const pm: PanelMarker | undefined = this.findPanelMarker(
                field,
                bwa,
              );

              if (pm && field.id === 'menstrualPhase') {
                return (
                  <td key={`v-${rowIdx}-${colIdx}`}>
                    {pm.altValue === MenstrualPhase.NONE ? (
                      <>
                        {MenstrualPhase.NONE}
                        <br />
                        (children&apos;s ranges)
                      </>
                    ) : (
                      pm.altValue
                    )}
                  </td>
                );
              }

              return (
                <td
                  key={`v-${rowIdx}-${colIdx}`}
                  className={pm ? this.getClassName(pm) : ''}
                >
                  {pm?.hasValue?.() ? this.displayValue(pm) : null}
                  &nbsp;&nbsp;
                  {pm ? this.getIcon(pm) : null}
                </td>
              );
            });

            const lastPm = this.findPanelMarker(
              field,
              bloodworkAnalysisHistory[bloodworkAnalysisHistory.length - 1],
            );

            // The marker whose reference range fills the six range columns.
            //
            // For a shipped marker this is always the newest test: its range
            // comes from the global BloodworkRange catalog, so every analysis
            // resolves one whether or not a value was entered. A CUSTOM marker
            // only carries a range on the tests it was actually typed into, so
            // one that was entered on an earlier test and left off the newest
            // would show six blank columns. Fall back to the most recent test
            // that does have a range — that is also the most recent version of
            // the range if the practitioner revised it between tests.
            const rangePm = this.findRangeBearingPanelMarker(
              field,
              bloodworkAnalysisHistory,
              lastPm,
            );

            let trend: React.ReactNode = null;
            if (
              lastPm?.hasValue?.() &&
              lastPm.previousPanelMarker?.hasValue?.() &&
              !(lastPm.isOptimal() && lastPm.previousPanelMarker.isOptimal())
            ) {
              const improved =
                Math.abs(lastPm.percentFromMean) <
                Math.abs(lastPm.previousPanelMarker.percentFromMean);
              const worsened =
                Math.abs(lastPm.percentFromMean) >
                Math.abs(lastPm.previousPanelMarker.percentFromMean);
              trend = improved ? (
                <img src={thumbs_up} alt="" />
              ) : worsened ? (
                <img src={thumbs_down} alt="" />
              ) : null;
            }

            return (
              <tr key={`r-${rowIdx}`}>
                <td>{field.getChartLabel()}</td>
                <td>{rangePm?.displayUnits?.(true) ?? field.getChartUnits()}</td>
                {valueCells}
                <td style={{ textAlign: 'center' }}>{trend}</td>
                {/*
                  displayBound() rather than the raw range number: it converts
                  to the display units AND rounds to the marker's precision.
                  These six columns previously printed the stored float
                  unrounded, so a derived bound could read "4.3999999".
                */}
                <td>{rangePm?.displayBound?.(rangePm?.range?.alarmMin) ?? ''}</td>
                <td>{rangePm?.displayBound?.(rangePm?.range?.labMin) ?? ''}</td>
                <td>{rangePm?.displayBound?.(rangePm?.range?.optimalMin) ?? ''}</td>
                <td>{rangePm?.displayBound?.(rangePm?.range?.optimalMax) ?? ''}</td>
                <td>{rangePm?.displayBound?.(rangePm?.range?.labMax) ?? ''}</td>
                <td>{rangePm?.range?.alarmMax ?? ''}</td>
              </tr>
            );
          })}
          {/* Under the charted rows, in first-appearance order. A result with
              no number cannot be sorted among ones that have them, and this is
              also the order a lab report prints: the measurements, then the
              observations. */}
          {textMarkers.map((textMarker, rowIdx) => {
            const expected = (textMarker.expected || '').trim();
            return (
              <tr key={`t-${rowIdx}`}>
                <td>{textMarker.label}</td>
                {/* No units: the words are the result. */}
                <td />
                {bloodworkAnalysisHistory.map((bwa, colIdx) => {
                  const text = this.textValueFor(textMarker, bwa);
                  const differs =
                    matchesExpectedText(text, expected) === false;
                  return (
                    <td
                      key={`tv-${rowIdx}-${colIdx}`}
                      className={
                        differs ? 'comparison-text-result-differs' : undefined
                      }
                    >
                      {text}
                    </td>
                  );
                })}
                {/* No trend: an arrow between two words would be claiming a
                    direction that does not exist. */}
                <td />
                {/* The six range columns have nothing to hold for a text
                    marker, so they carry the one thing it can be judged
                    against instead. */}
                <td colSpan={6} className="comparison-text-expected">
                  {expected ? `Expected: ${expected}` : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  practitionerNotesTable(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    isPdf: boolean = false,
  ) {
    // One row per collection date, carrying that test's Insights note AND its
    // Section Notes — a test whose notes were all typed in the section tabs has
    // no Insights note at all, and must still appear here.
    const notesArray = bloodworkAnalysisHistory
      .map(bwa => ({
        bloodwork: bwa.bloodwork,
        // Carried so the row can edit Section Notes, which is a consolidated
        // body rather than a stored field (see PractitionerNoteRow).
        bloodworkAnalysis: bwa,
        insights: insightsTextFor(bwa?.bloodwork),
        sectionNotes: sectionNotesTextFor(bwa),
      }))
      .filter(entry => entry.insights || entry.sectionNotes);

    if (notesArray.length === 0) {
      return null;
    }

    // Sort newer to older
    let sortedNotes = [...notesArray];
    sortedNotes.sort(
      (a, b) =>
        new Date(b.bloodwork.collectionDate).getTime() -
        new Date(a.bloodwork.collectionDate).getTime(),
    );

    // The Section Notes column only earns its width when the practitioner has
    // it switched on AND some test actually has section notes.
    const showSectionNotes =
      this.state.showSectionNotes && sortedNotes.some(e => !!e.sectionNotes);

    if (isPdf) {
      // Pre-paginated in JS: html2pdf can't split a table row, so each chunk is
      // one page's worth of rows and the heading repeats as "(cont.)".
      const noteChunks = paginateNoteRows(
        sortedNotes.map(entry => ({
          collectionDate: entry.bloodwork.collectionDate,
          insights: entry.insights,
          sectionNotes: entry.sectionNotes,
        })),
        { showSectionNotes },
      );

      return noteChunks.map((chunk, chunkIdx) => (
        // pageBreakInside: 'auto' overrides the .pdf-section avoid rule so the
        // wrapper isn't ALSO whole-pushed to a fresh page by html2pdf. Without it,
        // the wrapper push + the inner html2pdf__page-break stack into a double
        // break, leaving a blank page before each chunk. The inner .section-block
        // keeps its own page-break-inside: avoid, so the chunk table stays whole.
        // (Matches the Overall Progress & Trends chunk below.)
        <div
          key={`note-table-${chunkIdx}`}
          className="pdf-section"
          style={{ pageBreakInside: 'auto' }}
        >
          <div className="html2pdf__page-break" />
          <div style={{ height: TOP_OF_PAGE_SPACER }} />
          {/* Blue header + outline, matching the Disclaimer and Overall
              Progress & Trends cards. NOT "disclaimer-panel": that class is in
              html2pdf's break-inside:avoid list, which would stop these chunks
              from paginating. */}
          <div className="section-block">
            <Card style={{ marginTop: 0 }}>
              <Card.Header className="py-1">
                {showSectionNotes
                  ? 'Insights & Section Notes by Collection Date'
                  : 'Insights by Collection Date'}
                {chunkIdx > 0 ? ' (cont.)' : ''}
              </Card.Header>
              <Card.Body className="p-0">
                <Table
                  bordered
                  className="labsmartz-table labsmartz-notes-table"
                  style={{ marginTop: 0, marginBottom: 0, width: '100%' }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: showSectionNotes ? '15%' : '20%' }}>
                        Collection Date
                      </th>
                      <th style={showSectionNotes ? { width: '45%' } : undefined}>
                        Insights
                      </th>
                      {showSectionNotes && <th>Section Notes</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((item, idx) => {
                      const noteCell = (content: string, key: string) => (
                        <td>
                          <div
                            style={{
                              wordBreak: 'break-word',
                              color: NOTE_TEXT_COLOR,
                            }}
                          >
                            {content.split('\n').map((line: string, lIdx: number) => (
                              <div
                                key={`${key}-${lIdx}`}
                                style={{
                                  pageBreakInside: 'avoid',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {renderNoteLine(line)}
                              </div>
                            ))}
                          </div>
                        </td>
                      );
                      return (
                        // No zebra striping: these rows are text panels, not data
                        // rows, and a shaded one reads as a grey box beside the
                        // white Overall Progress & Trends card.
                        <tr key={`note-${chunkIdx}-${idx}`}>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {item.collectionDate}
                            </Moment>
                            {item.isCont && (
                              <span
                                style={{ fontSize: '0.8em', marginLeft: '4px' }}
                              >
                                (cont.)
                              </span>
                            )}
                          </td>
                          {noteCell(item.insightsSplit || '', 'ins')}
                          {showSectionNotes &&
                            noteCell(item.sectionSplit || '', 'sec')}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </div>
          <div style={{ height: '8px' }} />
        </div>
      ));
    }

    // For web UI, no chunking needed. Just show the sorted array.
    return (
      // Not striped — see labsmartz-notes-table in index.css.
      <Table bordered hover className="labsmartz-table labsmartz-notes-table">
        <thead>
          <tr>
            <th style={{ width: showSectionNotes ? '15%' : '20%' }}>
              Collection Date
            </th>
            <th style={showSectionNotes ? { width: '40%' } : undefined}>
              Insights
            </th>
            {showSectionNotes && (
              <th style={{ width: '37%' }}>Section Notes</th>
            )}
            <th style={{ width: showSectionNotes ? '8%' : '10%' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {sortedNotes.map((entry, idx) => {
            return (
              <PractitionerNoteRow
                // Keyed by test, not position: the row list is filtered to the
                // tests that have notes, so a test gaining its first note
                // shifts every index below it — and an index key would hand one
                // row's typed-but-unsaved note to a different test.
                key={entry.bloodwork.id || `idx-${idx}`}
                bloodwork={entry.bloodwork}
                bloodworkAnalysis={entry.bloodworkAnalysis}
                bloodworkEntryStore={this.props.bloodworkEntryStore}
                sectionNotesText={entry.sectionNotes}
                showSectionNotesColumn={showSectionNotes}
              />
            );
          })}
        </tbody>
      </Table>
    );
  }

  onHidePdfModal() {
    this.props.onHidePdfModal();
    this.setState({
      showPdfModal: false,
      numResultsToCompare: NUM_RESULTS_TO_COMPARE,
      withNotes: false,
    });
  }

  render() {
    const { bloodworkRanges, currentUserProfile } = this.props;
    const {
      showUpgradeModal,
      showPdfModal,
      numResultsToCompare,
      headerLogoSrc,
      furtherConsiderations,
    } = this.state;

    let { bloodworks } = this.props;
    if (!bloodworks || bloodworks.length === 0) {
      return null;
    }

    if (!currentUserProfile.showClientComparisonTable) {
      return null;
    }

    bloodworks = bloodworks.slice(0, numResultsToCompare);

    const bloodworkAnalysisHistory: BloodworkAnalysis[] = bloodworks
      .reverse()
      .map(bloodwork => {
        const bloodworkAnalysis = new BloodworkAnalysis({
          bloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        bloodworkAnalysis.init();
        bloodworkAnalysis.populatePanelMarkersArray();
        return bloodworkAnalysis;
      });

    if (bloodworkAnalysisHistory.length >= 2) {
      bloodworkAnalysisHistory[
        bloodworkAnalysisHistory.length - 1
      ].preparePreviousPanelMarkers(
        bloodworkAnalysisHistory[bloodworkAnalysisHistory.length - 2],
      );
    }
    const today = new Date().toISOString();
    const mostRecentBloodwork = bloodworks[bloodworks.length - 1];
    const actualClient =
      this.props.clientEntryStore?.clients?.find(
        c => c.id === mostRecentBloodwork.clientId,
      ) || mostRecentBloodwork.client;

    // Fallback logo if preview URL hasn’t resolved yet
    const fallbackLogo =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    const logoSrc = headerLogoSrc || fallbackLogo;
    console.log(
      '[BloodworkComparison] Render - headerLogoSrc:',
      headerLogoSrc ? `${headerLogoSrc.substring(0, 50)}...` : 'null',
    );

    // Check if the whiteBloodCells marker has any value across the history
    const whiteBloodCellsField = PanelField.ALL_FIELDS.find(
      f => f.id === 'whiteBloodCells',
    );
    const whiteBloodCellsHasValues = whiteBloodCellsField
      ? this.fieldHasAnyValue(whiteBloodCellsField, bloodworkAnalysisHistory)
      : false;

    return (
      <>
        <PDFModal
          show={showPdfModal}
          title="Test Results Comparison"
          client={actualClient}
          collectionDate={mostRecentBloodwork.collectionDate}
          elementId="pdf-comparison-results"
          onHide={() => this.onHidePdfModal()}
          preloadedLogoDataUrl={headerLogoSrc}
        >
          <div
            id="pdf-comparison-results"
            style={{ paddingBottom: 10, marginBottom: 10, transform: 'none' }}
          >
            <style>
              {`
                /* Page-break divs: force a break but take up no vertical space */
                #pdf-comparison-results .html2pdf__page-break {
                  height: 0 !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  border: 0 !important;
                  page-break-after: always !important;
                }

                #pdf-comparison-results h3,
                #pdf-comparison-results h5 {
                  margin-top: 8px !important;
                }

                #pdf-comparison-results table.labsmartz-table {
                  margin-top: 0 !important;
                }

                /* Keep each section's title glued to its table. This is the ONLY
                   "keep-together" primitive html2pdf 0.9.1 honors: break-after/
                   before:avoid are ignored by its pagebreak engine, but an element
                   with break-inside:avoid that is <= 1 page tall is pushed whole to
                   the next page when it would straddle a boundary (see
                   node_modules/html2pdf.js/src/plugin/pagebreaks.js, lines 104-113).
                   Sections that can exceed one page are forced onto a fresh page by
                   the renderer, so their title still lands at the top and is never
                   orphaned. */
                #pdf-comparison-results .pdf-section,
                #pdf-comparison-results .pdf-section .section-block {
                  break-inside: avoid;
                  page-break-inside: avoid;
                }

                /* Keep each data row intact. A tall section's table is allowed
                   to flow across a page boundary (see the isTall branch in
                   renderPdfSectionsAnalysisOrder); without this, html2pdf would
                   slice through a row mid-height at the break. */
                #pdf-comparison-results table.labsmartz-table tr {
                  break-inside: avoid;
                  page-break-inside: avoid;
                }

                /* The Disclaimer is pushed whole to a fresh page by the avoid
                   rule, which lands it EXACTLY on the page boundary — and the
                   slice grid rounds down to whole device pixels, so the top of
                   the blue header was being shaved onto the page above. The
                   padding is inside the pushed element, so those lost pixels
                   are blank. Same trick as .pdf-keep-together-block in
                   containers/pdf.css. */
                #pdf-comparison-results .pdf-disclaimer-block {
                  break-inside: avoid;
                  page-break-inside: avoid;
                  padding-top: 24px;
                }
              `}
            </style>
            <br />
            <br />
            <br />
            <div className="text-center">
              <Image
                id="logo"
                src={logoSrc}
                alt="Current Logo"
                fluid
                style={{
                  maxHeight: '100px',
                  maxWidth: '400px',
                  objectFit: 'contain',
                }}
                onError={(e: any) => {
                  const img = e.target as HTMLImageElement;
                  if (img.src !== fallbackLogo) {
                    img.src = fallbackLogo;
                  }
                }}
              />
            </div>
            <div>
              <br />
              <br />
              {/* Defaults to "Lab Comparison Report", not the old "Blood Work
                  Comparison Report", matching the analysis PDF's cover: a
                  comparison can line up an organic acids, stool or hair test
                  the practitioner added, so the specimen cannot be in the
                  title. Report Settings can replace it outright. */}
              <h3 className="text-center">
                <strong>
                  {resolveComparisonReportTitle(currentUserProfile)}
                </strong>
              </h3>
            </div>
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            {/* html2pdf__page-break will add a page break after the html element */}
            <div>
              <h5>
                <strong>Prepared for</strong>
              </h5>
              <h5>
                {actualClient.firstName} {actualClient.lastName}
              </h5>
              <br />
              {currentUserProfile.preparedBy && (
                <>
                  <h5>
                    <strong>Prepared by</strong>
                  </h5>
                  <h5>{currentUserProfile.preparedBy}</h5>
                  <br />
                </>
              )}
              <h5>
                <strong>Most Recent Collection Date</strong>
              </h5>
              <h5>
                <Moment format="MM/DD/YYYY">
                  {mostRecentBloodwork.collectionDate}
                </Moment>
              </h5>
              <br />
              <h5>
                <strong>Analysis Date</strong>
              </h5>
              <h5>
                <Moment format="MM/DD/YYYY">{today}</Moment>
              </h5>
              <br />
            </div>

            <div
              className="html2pdf__page-break"
              style={{ height: 0, margin: 0, padding: 0, border: 0 }}
            />

            {this.renderPdfSectionsAnalysisOrder(
              bloodworkAnalysisHistory,
              actualClient.gender,
              whiteBloodCellsHasValues,
            )}

            {/* Practitioner Notes in PDF */}
            {(() => {
              // Section notes count as notes here too — a test with section
              // notes and no Insights note still belongs in this block.
              const anyHasNotes = bloodworkAnalysisHistory.some(bwa =>
                comparisonNotesTextFor(bwa),
              );
              if (!this.state.withNotes || !anyHasNotes)
                return null;
              const notesTable = this.practitionerNotesTable(
                bloodworkAnalysisHistory,
                true,
              );
              return notesTable;
            })()}

            {/* Overall Practitioner Notes in PDF */}
            {(() => {
              if (!this.state.withNotes) return null;
              const noteText = actualClient?.overallPractitionerNotes || '';
              if (!noteText.trim()) return null;

              // Use the same pagination mechanism (see paginateNoteRows for
              // where the page geometry comes from: ~744 CSS px per page, and
              // why the safety margin sits here rather than in WRAP_CHARS).
              const MAX_PAGE_HEIGHT = 700;
              // Top spacer (TOP_OF_PAGE_SPACER) + card header + offsets.
              const HEADER_HEIGHT = 76;
              const ROW_PADDING = 12;
              const LINE_HEIGHT = 18;

              const lines = noteText.split('\n');
              const overallChunks: string[][] = [];
              let currentLines: string[] = [];
              let currentChunkHeight = HEADER_HEIGHT;

              lines.forEach(line => {
                // This box is the full width of a LANDSCAPE page: ~1063px of
                // text over a ~5.6px average character width, so ~189
                // characters on one rendered line. The REAL figure, no cushion
                // — counting a 160-char bullet as two lines (the old /120)
                // inflated the estimate by ~2x and broke the panel a page
                // early. The margin is in MAX_PAGE_HEIGHT instead.
                const WRAP_CHARS = 189;
                const wrapCount = Math.max(1, Math.ceil(line.length / WRAP_CHARS));
                const lineH = wrapCount * LINE_HEIGHT;

                if (
                  currentChunkHeight + lineH > MAX_PAGE_HEIGHT &&
                  currentLines.length > 0
                ) {
                  overallChunks.push(currentLines);
                  currentLines = [line];
                  currentChunkHeight = HEADER_HEIGHT + lineH;
                } else {
                  currentLines.push(line);
                  currentChunkHeight += lineH;
                }
              });
              if (currentLines.length > 0) overallChunks.push(currentLines);

              return overallChunks.map((linesChunk, idx) => (
                <div
                  key={`overall-chunk-${idx}`}
                  className="pdf-section"
                  style={{ pageBreakInside: 'auto' }}
                >
                  <div className="html2pdf__page-break" />
                  <div style={{ height: TOP_OF_PAGE_SPACER }} />
                  {/* Blue header + outline, matching the Disclaimer and the
                      Insights card. A plain Card, not "disclaimer-panel" \u2014
                      that class is in html2pdf's break-inside:avoid list and
                      would stop these chunks paginating. */}
                  <div className="section-block">
                    <Card style={{ marginTop: 0 }}>
                      <Card.Header className="py-1">
                        Overall Progress &amp; Trends{idx > 0 ? ' (cont.)' : ''}
                      </Card.Header>
                      <Card.Body
                        style={{
                          padding: '10px',
                          wordBreak: 'break-word',
                          minHeight: '2em',
                          color: NOTE_TEXT_COLOR,
                        }}
                      >
                        {linesChunk.map((line, lIdx, lArr) => (
                          <div
                            key={lIdx}
                            style={{
                              pageBreakInside: 'avoid',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {/* Formatted, like the on-screen field \u2014 the
                                practitioner's **bold** must not print as markup.
                                Still one div per line so the chunker's page math
                                is unchanged. */}
                            {renderNoteLine(line)}
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </div>
                  <div style={{ height: '12px' }} />
                </div>
              ));
            })()}
            {/* Disclaimer */}
            {/* No explicit html2pdf__page-break here: .disclaimer-panel is in
                html2pdf's `avoid` list (see PDFModal's pagebreak opts), so the
                panel is already pushed whole to a fresh page if it would straddle
                a boundary. A forced break ON TOP of that risks html2pdf's
                phantom-blank-page bug — when the preceding content ends exactly
                at a page edge, the forced "after" break expands its fill-pad to a
                full blank page, leaving the disclaimer page looking empty. Letting
                the keep-together rule place it avoids that entirely.

                The wrapper's padding-top is what stops the blue header being
                shaved onto the previous page. html2pdf's "avoid" pad lands the
                element on the page boundary EXACTLY (pagebreaks.js:97-100),
                and the canvas is then sliced on a grid rounded down to whole
                device pixels (worker.js:181) — so a boundary-hugging top edge
                loses a pixel or two to the page above. Padding INSIDE the
                pushed element gives those pixels something blank to eat.
                Mirrors .pdf-keep-together-block in containers/pdf.css. */}
            <div className="pdf-disclaimer-block">
              <DisclaimerSection furtherConsiderations={furtherConsiderations} />
            </div>
          </div>
        </PDFModal>

        {showPdfModal ? null : (
          <>
            {/* The DOM id stays `blood-work-comparison`: it is the scroll anchor
                bloodwork-analysis-container restores to, not something a
                practitioner reads. The heading says "Test Results Comparison" so
                it matches the rest of the practitioner chrome (the Test Results
                menu, "Test Results Analysis"). The PDF cover page keeps "Lab
                Comparison Report" by default: that document is the client's, and
                "Lab" covers an organic acids or hair panel without naming the
                specimen. */}
            <Card id="blood-work-comparison" ref={this.comparisonCardRef as any}>
              <Card.Header>
                Test Results Comparison&nbsp;
                <span
                  className="float-right d-flex align-items-center"
                  style={{ gap: '16px' }}
                >
                  {/* Export Raw Data is LIVE in all envs. The AI "Generate with
                      AI" summary below stays localhost-only (see gates further down). */}
                  {(
                    <>
                      <span
                        className="d-flex align-items-center"
                        style={{ gap: '14px' }}
                      >
                        <span>Export All Client Data</span>
                        <OverlayTrigger
                          trigger="click"
                          rootClose
                          overlay={this.exportPromptPopover()}
                          placement="bottom"
                        >
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            style={{
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '18px',
                            }}
                          />
                        </OverlayTrigger>
                        <OverlayTrigger
                          overlay={this.exportJsonTooltip()}
                          placement="bottom"
                        >
                          <FontAwesomeIcon
                            icon={faFileCode}
                            onClick={this.exportJson}
                            style={{
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '18px',
                            }}
                          />
                        </OverlayTrigger>
                      </span>
                      {this.headerDivider()}
                    </>
                  )}
                  <span
                    className="d-flex align-items-center"
                    style={{ gap: '14px' }}
                  >
                    <label
                      htmlFor="hideEmptyRowsToggle"
                      style={{
                        color: 'white',
                        marginBottom: '0',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Hide Blank Rows
                    </label>
                    <OverlayTrigger
                      overlay={this.hideBlankRowsTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon="info-circle"
                        style={{ cursor: 'help', fontSize: '18px' }}
                      />
                    </OverlayTrigger>
                    <input
                      id="hideEmptyRowsToggle"
                      type="checkbox"
                      checked={!this.state.showHiddenRows}
                      onChange={e =>
                        this.setState(
                          { showHiddenRows: !e.target.checked },
                          this.scrollComparisonTitleIntoView,
                        )
                      }
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        backgroundColor: 'white', // white background box
                        border: '1px solid white', // blends with header
                        borderRadius: '2px',
                        position: 'relative',
                      }}
                    />
                  </span>
                  {this.headerDivider()}
                  {/* Each export icon carries its own label and is one click
                      target with it — the same treatment as the Test Results
                      Analysis header. The two download glyphs are near
                      identical, so a single shared "Export to PDF" label left
                      the with/without-notes choice to a hover tooltip. */}
                  <span
                    className="d-flex align-items-center"
                    style={{ gap: '14px' }}
                  >
                    <OverlayTrigger
                      overlay={this.exportInfoTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon="info-circle"
                        onClick={this.openExportInfoWindow}
                        style={{ cursor: 'pointer', fontSize: '18px' }}
                      />
                    </OverlayTrigger>
                    <OverlayTrigger
                      overlay={this.exportTooltip()}
                      placement="bottom"
                    >
                      <span
                        onClick={() => this.export(false)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          color: 'white',
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Export to PDF without notes
                        <FontAwesomeIcon
                          icon={faFileDownload}
                          style={{ fontSize: '18px' }}
                        />
                      </span>
                    </OverlayTrigger>
                    <OverlayTrigger
                      overlay={this.exportWithNotesTooltip()}
                      placement="bottom"
                    >
                      <span
                        onClick={() => this.export(true)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          color: 'white',
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Export to PDF with notes
                        <FontAwesomeIcon
                          icon={faFileDownloadSolid}
                          style={{ fontSize: '18px' }}
                        />
                      </span>
                    </OverlayTrigger>
                  </span>
                </span>
              </Card.Header>
              <Card.Body>
                {/* Sections render in the test-results analysis order
                    (see comparisonSectionOrder). */}
                {this.buildComparisonSections(
                  bloodworkAnalysisHistory,
                  actualClient.gender,
                  whiteBloodCellsHasValues,
                ).map(section => (
                  <React.Fragment key={section.key}>
                    <div style={{ height: '8px' }} />
                    <h5>
                      <strong>{section.title}</strong>
                    </h5>
                    {section.table}
                    <div style={{ height: '8px' }} />
                  </React.Fragment>
                ))}

                {/* Insights note OR section notes — either one earns the block. */}
                {(() => {
                  const anyNotes = bloodworkAnalysisHistory.some(bwa =>
                    comparisonNotesTextFor(bwa),
                  );
                  if (!anyNotes) return null;
                  // The toggle only appears when there are section notes to
                  // show, and the title follows what the table is actually
                  // rendering (same rule as practitionerNotesTable).
                  const anySectionNotes = bloodworkAnalysisHistory.some(bwa =>
                    sectionNotesTextFor(bwa),
                  );
                  const showingSectionNotes =
                    this.state.showSectionNotes && anySectionNotes;
                  return (
                    // Blue header + outline, so this reads as the same kind of
                    // panel as Overall Progress & Trends and the Disclaimer.
                    <Card className="mt-4">
                      <Card.Header className="d-flex align-items-center justify-content-between">
                        <span>
                          {showingSectionNotes
                            ? 'Insights & Section Notes by Collection Date'
                            : 'Insights by Collection Date'}
                        </span>
                        {anySectionNotes && (
                          <span
                            className="d-flex align-items-center"
                            style={{ gap: '10px' }}
                          >
                            <label
                              htmlFor="showSectionNotesToggle"
                              style={{
                                marginBottom: 0,
                                fontWeight: 500,
                                cursor: 'pointer',
                              }}
                            >
                              Show Section Notes
                            </label>
                            <OverlayTrigger
                              overlay={this.showSectionNotesTooltip()}
                              placement="left"
                            >
                              <FontAwesomeIcon
                                icon="info-circle"
                                style={{ cursor: 'help', fontSize: '16px' }}
                              />
                            </OverlayTrigger>
                            {/* lsm-styled-checkbox carries the white-on-blue box
                                AND the :checked checkmark (App.css) — a plain
                                inline-styled box would render blank when on. */}
                            <input
                              id="showSectionNotesToggle"
                              className="lsm-styled-checkbox"
                              type="checkbox"
                              checked={this.state.showSectionNotes}
                              onChange={e =>
                                this.handleShowSectionNotesChange(
                                  e.target.checked,
                                )
                              }
                            />
                          </span>
                        )}
                      </Card.Header>
                      <Card.Body className="p-0">
                        {this.practitionerNotesTable(
                          bloodworkAnalysisHistory,
                          false,
                        )}
                      </Card.Body>
                    </Card>
                  );
                })()}

                {/* id anchors Save's scroll-back to the top of this card. */}
                <Card className="mt-4" id="overall-progress">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <span>Overall Progress & Trends</span>
                    {aiProgressSummaryVisible() && (
                      <Button
                        size="sm"
                        variant="light"
                        onClick={this.onGenerateSummary}
                        disabled={this.state.generatingSummary}
                      >
                        {this.state.generatingSummary
                          ? 'Generating…'
                          : 'Generate with AI'}
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body>
                    {aiProgressSummaryVisible() && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#a0522d',
                          fontWeight: 600,
                          marginBottom: '6px',
                        }}
                      >
                        ⚠️ AI-generated draft. This text appears on the client's
                        report — please review and edit it before saving and
                        printing.
                      </div>
                    )}
                    <ClientNotes
                      title="Add your own notes here. Click Save when finished."
                      client={actualClient}
                      clientEntryStore={this.props.clientEntryStore}
                      aiDraft={this.state.aiSummaryDraft}
                      scrollAnchorId="overall-progress"
                    />
                  </Card.Body>
                </Card>

              {/* Disclaimer in UI */}
              <div className="mt-4">
                <DisclaimerSection furtherConsiderations={furtherConsiderations} />
              </div>

              </Card.Body>
            </Card>



            <GenericModal
              show={showUpgradeModal}
              title="Export to PDF Feature Not Available"
              text="Export to PDF feature is not available with a free account."
              link="https://labsmarts.com/pricing"
              linkText="Click here to upgrade."
              onHide={() => this.setState({ showUpgradeModal: false })}
            />
          </>
        )}
      </>
    );
  }
}
